import type { ChartUserOverrides, DataTableUserOverrides } from "@coeff/agent-types";
import type * as AgGridGlobals from "ag-grid-community";
import React, { useLayoutEffect } from "react";

import { createCellRevealTooltipComponent } from "./cellRevealPanel";
import {
  applyChartLabelEnforcement,
  formatChartNumber,
  truncateChartLabel,
  truncateLegendItems,
} from "./chartLabelEnforcement";
import { adoptChartRealm } from "./chartOptionsRealm";
import { applyChartUserOverrides } from "./chartUserOverrides";
import { writeTextToClipboardWithFallback } from "./clipboard";
import {
  computeTableContentHeightReport,
  ResolvedTableGridHeight,
  resolveTableGridHeight,
  TableContentHeightReport,
} from "./dataTableSizing";
import { buildGeoValueMap, normalizeGeoName } from "./geoNames";
import { slicerClause } from "./slicerValues";
import { Color, ensureContrast } from "./themes/color";
import { ChartElementColor, resolveDatasetColors, SemanticTokens } from "./themes/colorResolver";
import { isCustomThemeId } from "./themes/themeIds";
import { DEFAULT_SEMANTIC_TOKENS, Theme } from "./themes/types";
import { CleanupFunction, RunSQLFn, SlicerState, SlicerValue } from "./useDashboardRuntime";

// --------------------------------
// Types for iframe window globals
// --------------------------------

export interface FlatpickrInstance {
  destroy: () => void;
  close: () => void;
  setDate: (dates: Date[], triggerChange?: boolean) => void;
  selectedDates: Date[];
  input: HTMLInputElement;
  calendarContainer: HTMLElement;
  config: {
    minDate: Date;
    maxDate: Date;
    defaultDate?: [Date, Date];
  };
}

// Chart.js Chart type (constructor)
interface ChartConstructor {
  new (ctx: CanvasRenderingContext2D, config: unknown): { destroy: () => void };
  getChart?: (
    canvas: HTMLCanvasElement | CanvasRenderingContext2D
  ) => { destroy: () => void } | undefined;
}

interface GoogleAccounts {
  id: {
    initialize: (config: {
      client_id: string;
      ux_mode: string;
      login_uri: string;
      state: string;
    }) => void;
    renderButton: (
      element: HTMLElement,
      options: {
        type: string;
        theme: string;
        size: string;
        text: string;
        width: number;
      }
    ) => void;
  };
}

interface Google {
  accounts: GoogleAccounts;
}

export interface DashboardWindow extends Window {
  // Constructors from iframe context (needed for cross-frame instanceof checks)
  Date: typeof Date;
  Array: typeof Array;
  // Third-party libraries loaded in iframe
  Chart: ChartConstructor;
  agGrid: typeof AgGridGlobals;

  // Current dashboard theme (set by theme system)
  __coeffWebDashboardTheme?: Theme;
  // Pre-built AG Grid theme object (set by theme system)
  __coeffAGGridTheme?: AgGridGlobals.Theme;
  marked: {
    parse: (markdown: string) => string;
  };
  flatpickr: ((
    element: string | HTMLElement,
    options: Record<string, unknown>
  ) => FlatpickrInstance) & {
    formatDate: (date: Date, format: string) => string;
  };
  google?: Google;

  // Some TS lib configs don't include Window.console, but the iframe always has it.
  console: Console;

  // Function constructor for evaluating JS in iframe context
  Function: FunctionConstructor;

  // Dashboard helper functions exposed globally
  Helpers: Record<string, unknown>;
  runSQL?: RunSQLFn;
  createChart?: typeof createChart;
  createDataTable?: typeof createDataTable;
  createModalDataTable?: typeof createModalDataTable;
  createMetricCardDrilldown?: typeof createMetricCardDrilldown;
  createDrilldown?: typeof createDrilldown;
  sortChartDataByDateLabels?: typeof sortChartDataByDateLabels;
  parseDateLabelToSortKey?: typeof parseDateLabelToSortKey;
  formatChartNumber?: typeof formatChartNumber;
  truncateChartLabel?: typeof truncateChartLabel;
  interpolateHeatmapColor?: (
    value: number,
    min: number,
    max: number,
    lowColor?: string,
    highColor?: string
  ) => string;
  normalizeGeoName?: typeof normalizeGeoName;
  buildGeoValueMap?: typeof buildGeoValueMap;
  slicerClause?: typeof slicerClause;

  // Legacy JS code injection entry point
  initDashboard?: () => void;

  // Optional DOM-to-image screenshot library (loaded in iframe for block screenshots)
  html2canvas?: (
    element: HTMLElement,
    options?: Record<string, unknown>
  ) => Promise<HTMLCanvasElement>;

  // Bottom drawer for modal drill-downs
  setBottomDrawerContent?: (content: HTMLElement[] | null) => void;
  // Open the dashboard chat sidebar in the parent app
  openDashboardChat?: () => void;
  openDashboardChatWithBlockChip?: (blockId: string, blockTitle: string, blockType: string) => void;
  // Programmatically set a slicer value, e.g. from a custom_html block's buttons.
  // Mirrors the native slicer UI so custom filter blocks can drive filtering.
  setSlicerValue?: (slicerName: string, value: SlicerValue) => void;

  // Viewer identity (email of the logged-in viewer, used by personalized view blocks)
  __coeff_personalized_view?: { email: string | null };

  // -----------------------------
  // Console capture (auto-fix)
  // -----------------------------
  __coeffConsoleCaptureInstalled?: boolean;
  __coeffConsoleCaptureCleanup?: () => void;
  __coeffConsoleSeq?: number;
  __coeffConsoleBuffer?: import("./useDashboardConsoleCapture").CoeffConsoleEvent[];
}

// --------------------------------
// Types
// --------------------------------

export type RenderFunction = (
  cardElement: HTMLElement,
  slicerState: SlicerState,
  runSQL: RunSQLFn
) => Promise<CleanupFunction | void> | CleanupFunction | void;

export interface ChartOptions {
  type: string;
  data: {
    labels: string[];
    datasets: Array<{
      label: string;
      data: unknown[];
      [key: string]: unknown;
    }>;
  };
  options?: Record<string, unknown>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  plugins?: any[];
}

export interface CellRendererParams {
  value: unknown;
  data: Record<string, unknown>;
  /** AG Grid row node. `rowPinned` is "bottom" for the pinned total row. */
  node?: { rowPinned?: string | null } | null;
}

type ColumnCellClass = string | string[] | ((params: CellRendererParams) => string | string[]);

/**
 * Themed surface for popovers and panels floating above the dashboard (edit popovers, the KPI
 * icon picker).
 *
 * Two layers on purpose. `--color-card-background` may be translucent, which would let dashboard
 * content show through a floating panel, so it is painted as a flat one-stop gradient over an
 * opaque `--color-main-background` fill rather than set directly as background-color.
 */
export const CARD_SURFACE_STYLE: React.CSSProperties = {
  backgroundColor: "var(--color-main-background, #fff)",
  backgroundImage: "linear-gradient(var(--color-card-background), var(--color-card-background))",
};

/**
 * AG Grid themes set background-color on .ag-row, overriding Tailwind bg-*
 * utility classes due to CSS specificity/cascade order. Adding Tailwind's `!`
 * prefix makes them !important so they win.
 */
export function addImportantToBgClasses(classStr: string): string {
  return classStr
    .split(/\s+/)
    .map(cls => {
      if (/(?:^|:)bg-/.test(cls) && !cls.includes("!bg-")) {
        return cls.replace(/bg-/, "!bg-");
      }
      return cls;
    })
    .join(" ");
}

/**
 * Merges framework cell classes (highlight, alignment) with optional user
 * `cellClass` from dashboard column definitions. Matches AG Grid expectations:
 * either a static string[] or a per-cell function returning string[].
 */
export function mergeColumnCellClass(
  internalCellClasses: string[],
  columnCellClass: ColumnCellClass | undefined
): string[] | ((params: CellRendererParams) => string[]) {
  if (!columnCellClass) {
    return internalCellClasses;
  }
  if (typeof columnCellClass === "function") {
    const userFn = columnCellClass;
    return (params: CellRendererParams) => {
      const userResult = userFn(params);
      const userClasses = Array.isArray(userResult) ? userResult : userResult ? [userResult] : [];
      return [...internalCellClasses, ...userClasses];
    };
  }
  const userClasses = Array.isArray(columnCellClass) ? columnCellClass : [columnCellClass];
  return [...internalCellClasses, ...userClasses];
}

interface ColumnDef {
  field: string;
  headerName?: string;
  formatValue?: (value: unknown) => string;
  highlight?: boolean;
  /** Explicit pixel width for the column. Setting this on any column switches the
   * table's auto-size strategy from fit-to-grid to provided-width: the column is pinned
   * to this width and excluded from the `fitGridWidth` pass (via `suppressSizeToFit`), so
   * it is honored instead of being stretched/shrunk. Other columns still size-to-fit. */
  width?: number;
  /** Proportional width: AG Grid distributes the available space across flex
   * columns by ratio (a column with flex:2 is twice as wide as flex:1). */
  flex?: number;
  /** Freeze (pin) the column to one side so it stays visible while the rest of
   * the table scrolls horizontally. */
  pinned?: "left" | "right";
  /** Every column already gets a hover tooltip, but only when its text is actually clipped.
   * That is the right default for ordinary data and the wrong one for a column whose tooltip is
   * the whole point: an explanatory note has nothing to do with column width, and a `formatValue`
   * that shortens text stops the cell overflowing at all — which would hide the very tooltip
   * meant to reveal the full value.
   *
   * Set this to take a column out of that gate, so its tooltip shows on every hover. `true` shows
   * the raw cell value (the "reveal what formatValue shortened" case), a string shows a static
   * note, and a function receives the cell params and returns the text. `false` turns the
   * column's tooltip off entirely. Leave it unset to keep the quieter clipped-only default.
   *
   * Leaving it unset is also the right call for a drill-down text column: those gate on a JS
   * measurement instead, which already reveals the full raw value in a copyable panel whenever
   * the cell doesn't fit — including when its own formatValue is what cut the text.
   *
   * Quirk worth knowing: opting in works by giving the column a pass-through `cellRenderer` (see
   * buildDefaultCellRenderer), because "is a renderer column" is what AG Grid uses to decide
   * which cells skip truncation measurement. What the reader sees is unchanged, but the column's
   * text then reaches the DOM through innerHTML rather than textContent — escaped on the way —
   * and renders via a cell component instead of plain text. */
  tooltip?: boolean | string | ((params: CellRendererParams) => string);
  /**
   * Returns an HTML string to render in the cell, replacing the default
   * formatValue display. Returning null/undefined means "no override" — the cell
   * falls back to formatValue / default rendering, matching the `cellStyle`
   * convention where `{}` means no override. Return `''` to render an empty cell.
   */
  cellRenderer?: (params: CellRendererParams) => string | null | undefined;
  /**
   * Turn the cell into a link: the value renders underlined in the cell's own text color, with a
   * small external-link icon that fades in on hover. Return the URL to link to, or null/empty for
   * no link on that row — those rows stay plain, un-underlined text. Only http(s) URLs are
   * honored. The display text is the column's formatValue output (or the raw value). Ignored when
   * `cellRenderer` is also set (the explicit renderer wins).
   */
  link?: string | ((params: CellRendererParams) => string | null | undefined);
  cellStyle?: Record<string, string> | ((params: CellRendererParams) => Record<string, string>);
  cellClass?: string | string[] | ((params: CellRendererParams) => string | string[]);
  /** Minimum column width in px. Honored by `fitGridWidth` as a floor (overrides the table default). */
  minWidth?: number;
  /** Maximum column width in px. */
  maxWidth?: number;
  /**
   * Custom AG Grid sort comparator, `(valueA, valueB) => number`. Use it to sort a
   * display-label column by an underlying key — e.g. a week/month column whose cells
   * hold "Jul 21, 2025" should sort by date, not alphabetically. When omitted, columns
   * whose values are date-like label strings get a chronological comparator
   * automatically; everything else uses AG Grid's default. */
  comparator?: (valueA: unknown, valueB: unknown) => number;
}

/** What a table's `onRowClick` receives: the row keyed by column `field`, plus the clicked cell. */
export interface RowClickParams {
  data: Record<string, unknown>;
  rowIndex: number;
  field?: string;
  value?: unknown;
}

interface RowParams {
  data: Record<string, unknown> | undefined;
}

interface DataTableOptions {
  columns: ColumnDef[] | string[];
  data: unknown[][];
  pinnedBottomRowData?: unknown[][];
  /**
   * Recomputes the pinned Total/summary row(s) from the rows currently visible
   * after in-grid column filters and the search box. Receives the visible rows in
   * the same `data` shape (arrays in column order) and returns
   * `pinnedBottomRowData`-shaped rows. Runs once on creation with all rows, so it
   * replaces a static `pinnedBottomRowData`.
   */
  computePinnedBottomRowData?: (filteredRows: unknown[][]) => unknown[][];
  getRowStyle?: (params: RowParams) => Record<string, string> | undefined;
  getRowClass?: (params: RowParams) => string | string[] | undefined;
  /** @deprecated Tables now use infinite scroll (no pagination). Still accepted
   * for backwards-compat with existing render JS, but no longer has any effect. */
  pageSize?: number;
  /**
   * Row drill-down: the one supported click hook on a table. Fires for a click on a row's
   * cell, skipping the pinned total row, links/buttons inside cells, and clicks that end a
   * text selection. Return `ModalDataTableOptions` (sync or async) to open those rows in the
   * drill-down drawer; return nothing to handle the click yourself.
   */
  onRowClick?: (
    params: RowClickParams
  ) =>
    | void
    | ModalDataTableOptions
    | null
    | undefined
    | Promise<void | ModalDataTableOptions | null | undefined>;
}

export function wrapGetRowStyleForAgGrid(
  getRowStyle: DataTableOptions["getRowStyle"]
): DataTableOptions["getRowStyle"] {
  if (!getRowStyle) {
    return undefined;
  }
  return (params: RowParams) => {
    if (!params.data) {
      return undefined;
    }
    return withReadableTextColor(getRowStyle(params));
  };
}

export function wrapGetRowClassForAgGrid(
  getRowClass: DataTableOptions["getRowClass"]
): ((params: RowParams) => string | undefined) | undefined {
  if (!getRowClass) {
    return undefined;
  }
  return (params: RowParams) => {
    if (!params.data) {
      return undefined;
    }
    const result = getRowClass(params);
    if (!result) {
      return result;
    }
    const classStr = Array.isArray(result) ? result.join(" ") : result;
    return addImportantToBgClasses(classStr);
  };
}

interface ModalDataTableOptions extends DataTableOptions {
  title: string;
}

type DataTableExportApi = {
  getCsvText: () => string;
  copyCsvToClipboard: () => Promise<void>;
  downloadCsv: () => void;
  /** Apply a quick-filter string (drives the block-chrome search box). */
  setQuickFilter: (text: string) => void;
};

type CoeffDataTableExportHost = HTMLElement & { __coeffDataTableExportApi?: DataTableExportApi };

type DrilldownChatChip = { blockId: string; blockTitle: string; blockType: string };
type DrilldownChatChipHost = HTMLElement & { __coeffDrilldownChatChip?: DrilldownChatChip };
// Two-tier column-width floors.
//
//  * DATA_TABLE_MIN_COLUMN_WIDTH (140) is the LAYOUT floor for every auto-fit
//    pass. AG Grid only applies this when it's passed explicitly: the initial
//    layout gets it via autoSizeStrategy.defaultMinWidth, and every subsequent
//    imperative refit must pass it to sizeColumnsToFit({ defaultMinWidth }) — see
//    sizeColumnsToFitIfWideEnough. When present, the fit floors each spread column
//    at max(defaultMinWidth, colDef.minWidth) = 140. WITHOUT it the fit falls back
//    to the column's own minWidth (48) and crushes unpinned columns.
//  * DATA_TABLE_MIN_RESIZE_WIDTH (48) is defaultColDef.minWidth — the floor AG Grid
//    enforces on a manual drag-resize. Drag-resize does NOT route through
//    sizeColumnsToFit, so this stays decoupled from the layout floor above.
//
// Net: a fresh build (and any refit) lays columns out at >=140, yet a user can
// hand-shrink one down to 48 and it sticks (the drag is remembered and excluded
// from the fit via suppressSizeToFit).
const DATA_TABLE_MIN_COLUMN_WIDTH = 140;
const DATA_TABLE_MIN_RESIZE_WIDTH = 48;

/**
 * Session-scoped memory of user-dragged column widths, keyed by
 * `${dashboardId}:${cardElementId}`. Lets a table re-render — theme switch,
 * slicer change, in-place refresh — keep the widths the user resized, without a
 * backend schema change. Only the columns the user actually resized are stored,
 * and only those that still exist are reapplied; added/removed columns fall back
 * to the fitGridWidth pass. Card element ids are `block_${blockId}` where blockId
 * is an LLM-generated semantic name ("salesTable") unique only within one
 * dashboard — the dashboard-id prefix (see {@link _dataTableMemoryNamespace})
 * keeps same-named blocks on different dashboards from sharing state across SPA
 * navigation. For editors these widths are also persisted into the block's
 * `table_overrides` (see {@link _onTableOverridesChange}); this map remains the
 * latest-drag layer on top of that, and the only layer for view-only users.
 */
const _columnWidthMemory = new Map<string, Record<string, number>>();

/**
 * Session-scoped memory of user-dragged column display order, keyed the same way
 * as {@link _columnWidthMemory} and layered over persisted `table_overrides` the
 * same way widths are: a re-render (theme switch, slicer change, refresh) rebuilds
 * the grid from the JS column definitions, and this map re-applies the order the
 * user dragged this session.
 */
const _columnOrderMemory = new Map<string, string[]>();

/**
 * Session-scoped memory of user-typed column header renames, keyed the same way
 * as {@link _columnWidthMemory}. Unlike the width map (a per-key delta), each
 * entry is the block's COMPLETE alias map — seeded from the persisted
 * `table_overrides.column_headers` on first edit — so clearing an alias back to
 * the original name (key deleted) isn't resurrected by the persisted layer.
 */
const _columnHeaderMemory = new Map<string, Record<string, string>>();

/**
 * Session-scoped memory of the active quick-filter text, keyed the same way as
 * {@link _columnWidthMemory}. The search box lives in the React block chrome
 * (persistent), but a theme switch / slicer change / refresh recreates the
 * AG Grid instance unfiltered — leaving the box showing a query over unfiltered
 * rows. Re-seeding the grid's quickFilterText from here on creation keeps the
 * rendered rows in sync with the visible search text across re-renders.
 */
const _quickFilterMemory = new Map<string, string>();

/**
 * The session-remembered quick-filter text for a block's data table, if any.
 * Lets the React search chrome (DraggableBlock) re-hydrate on mount so the
 * visible search box matches the filter the recreated grid re-seeds from
 * {@link _quickFilterMemory} — without it, a remount (navigate away and back,
 * block remount) shows filtered rows with an empty, collapsed search box.
 */
export function getSessionQuickFilter(blockId: string): string | undefined {
  return _quickFilterMemory.get(tableSessionKey(blockId)) || undefined;
}

/**
 * Dashboard id prefix for the session memory maps above, set by
 * {@link useDashboardWindowGlobals} for the currently mounted dashboard. The
 * module (and the maps) outlive SPA navigation between dashboards, so keys must
 * be namespaced per dashboard to prevent a filter/width from one dashboard's
 * "salesTable" leaking into another's.
 */
let _dataTableMemoryNamespace: string | null = null;

/**
 * Persisted manual table adjustments (column widths/order) per block id, read
 * from each data_table block's `table_overrides` in the content spec and set by
 * {@link useDashboardWindowGlobals}. Applied at grid creation on top of the JS
 * column definitions, then layered under the session memory maps above (which
 * hold anything dragged since the last save).
 */
let _tableOverridesByBlockId: Record<string, DataTableUserOverrides> = {};

/**
 * Persisted manual chart adjustments (type, stacking, legend, data labels, axis
 * visibility) per block id, read from each chart block's `chart_overrides` in the
 * content spec and set by {@link useDashboardWindowGlobals}. Applied by createChart on
 * top of the Chart.js config the render JS builds (see chartUserOverrides.ts).
 */
let _chartOverridesByBlockId: Record<string, ChartUserOverrides> = {};

/**
 * Editor-only persistence hook for manual table adjustments, set by
 * {@link useDashboardWindowGlobals}. Called with the complete overrides for a
 * block after the user finishes a column drag (resize or reorder); the viewer
 * patches them into the block's `table_overrides` and saves the dashboard.
 * Null for view-only users — their drags stay session-scoped.
 */
let _onTableOverridesChange: ((blockId: string, overrides: DataTableUserOverrides) => void) | null =
  null;

/**
 * Reports a data table's natural card content height (title + rows + footer +
 * card padding) after each sizing pass, set by {@link useDashboardWindowGlobals}.
 * The layout uses it to auto-collapse a table-only row to its content — see
 * computeEffectiveRowHeight in layoutHelpers — instead of showing a card of
 * blank space under a short table.
 */
let _onTableContentHeight: ((blockId: string, heights: TableContentHeightReport) => void) | null =
  null;

/**
 * Last-seen fingerprint (`JSON(table_overrides)|render_function_js`) per
 * namespaced block key. Unlike the maps above this survives dashboard unmount:
 * it's how {@link invalidateStaleTableSessionMemory} tells "the same block state
 * I already layered session drags over" apart from "the block changed underneath
 * me" (AI edit, refresh, another editor's save) — the session layer must yield
 * in the second case or a user's old drag shadows the AI's change forever and
 * the next drag writes the zombie values back into table_overrides.
 */
const _tableStateFingerprints = new Map<string, string>();

/** The session-memory key for a block in the current dashboard namespace. */
function tableSessionKey(blockId: string): string {
  return `${_dataTableMemoryNamespace ?? ""}:block_${blockId}`;
}

/**
 * Drops session-scoped column adjustments (widths, order, header renames) for
 * every block whose persisted state changed since we last saw it, then records
 * the new fingerprints. Called by {@link useDashboardWindowGlobals} on every
 * content-spec change, BEFORE the grids re-create (layout effect vs the blocks'
 * passive render effect), so a recreated grid layers session memory only over
 * the persisted state those drags were made on. First sight of a block records
 * its fingerprint without clearing, and other namespaces' entries are untouched.
 * Exported for tests.
 */
export function invalidateStaleTableSessionMemory(
  fingerprintByBlockId: Record<string, string>
): void {
  const prefix = `${_dataTableMemoryNamespace ?? ""}:block_`;
  const currentKeys = new Set(Object.keys(fingerprintByBlockId).map(tableSessionKey));
  // Blocks deleted from the dashboard: drop their fingerprint and ALL session
  // leftovers — including the quick filter, or a later block reusing the id
  // would render silently pre-filtered by the dead block's query.
  for (const key of [..._tableStateFingerprints.keys()]) {
    if (key.startsWith(prefix) && !currentKeys.has(key)) {
      _tableStateFingerprints.delete(key);
      _columnWidthMemory.delete(key);
      _columnOrderMemory.delete(key);
      _columnHeaderMemory.delete(key);
      _quickFilterMemory.delete(key);
    }
  }
  for (const [blockId, fingerprint] of Object.entries(fingerprintByBlockId)) {
    const key = tableSessionKey(blockId);
    const lastSeen = _tableStateFingerprints.get(key);
    if (lastSeen !== undefined && lastSeen !== fingerprint) {
      _columnWidthMemory.delete(key);
      _columnOrderMemory.delete(key);
      _columnHeaderMemory.delete(key);
    }
    _tableStateFingerprints.set(key, fingerprint);
  }
}

export function restoreTableSessionOverrides(
  blockId: string,
  overrides: DataTableUserOverrides
): void {
  const key = tableSessionKey(blockId);
  if (overrides.column_widths && Object.keys(overrides.column_widths).length > 0) {
    _columnWidthMemory.set(key, { ...overrides.column_widths });
  }
  if (overrides.column_order && overrides.column_order.length > 0) {
    _columnOrderMemory.set(key, [...overrides.column_order]);
  }
  if (overrides.column_headers && Object.keys(overrides.column_headers).length > 0) {
    _columnHeaderMemory.set(key, { ...overrides.column_headers });
  }
  _tableStateFingerprints.delete(key);
}

/** Test-only access to the module session-memory maps. */
export const __dataTableSessionMemoryForTests = {
  widths: _columnWidthMemory,
  order: _columnOrderMemory,
  headers: _columnHeaderMemory,
  quickFilter: _quickFilterMemory,
  fingerprints: _tableStateFingerprints,
  setNamespace: (namespace: string | null): void => {
    _dataTableMemoryNamespace = namespace;
  },
};

// --------------------------------
// Module-level reference to iframe window
// Set by useDashboardHelpers hook
// --------------------------------

let _targetWindow: DashboardWindow | null = null;

function getTargetWindow(): DashboardWindow {
  if (!_targetWindow) {
    throw new Error("Target window not set. Ensure useDashboardHelpers is called first.");
  }
  return _targetWindow;
}

// --------------------------------
// Color Manipulation Helpers
// --------------------------------

/**
 * Convert hex color to rgba with opacity.
 */
function hexToRgba(hex: string, opacity: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/**
 * Interpolate between two hex colors based on a value within a range.
 * Returns a CSS rgb() color string suitable for inline styles (e.g. AG Grid cellStyle).
 * Uses per-channel RGB lerp so color transitions are perceptually smooth.
 */
export function interpolateHeatmapColor(
  value: number,
  min: number,
  max: number,
  lowColor = "#ffffff",
  highColor = "#4f46e5"
): string {
  const t =
    !Number.isFinite(value) || !Number.isFinite(min) || !Number.isFinite(max) || max === min
      ? 0
      : Math.max(0, Math.min(1, (value - min) / (max - min)));
  const lerp = (a: number, b: number) => Math.round(a + (b - a) * t);
  // Parse via Color so theme-derived endpoints (rgba/hsl/oklch, e.g. a card background) work, not
  // just hex. Falls back to the nearer endpoint string if either color can't be parsed.
  const low = Color.tryParse(lowColor);
  const high = Color.tryParse(highColor);
  if (!low || !high) {
    return t >= 0.5 ? highColor : lowColor;
  }
  return `rgb(${lerp(low.r, high.r)}, ${lerp(low.g, high.g)}, ${lerp(low.b, high.b)})`;
}

// Readable text colors for auto-contrast; near-black / near-white so they pass contrast against
// any solid cell/row background regardless of the active theme.
const CONTRAST_DARK_TEXT = "#111827";
const CONTRAST_LIGHT_TEXT = "#F9FAFB";

// WCAG AA for normal text; chart labels/ticks and table cells are small, so the 3:1 "large text"
// bar is too lenient.
const WCAG_AA_NORMAL_TEXT = 4.5;

/** Live card surface a table row/cell sits on, used as the fallback contrast background. */
function getThemeCardBackground(): string | undefined {
  return _targetWindow?.__coeffWebDashboardTheme?.tailwind?.card?.backgroundColor;
}

/**
 * Backward-compat safeguard for AI-generated blocks that hardcode colors in
 * `getRowStyle`/`cellStyle`. Two failure modes, both from crossing light↔dark:
 * - a hardcoded LIGHT background hex (e.g. `#f0fdf4`, `#ffffff`→heatmaps) under a dark theme, whose
 *   inherited text color is light → we derive a contrasting `color` from the background luminance;
 * - a hardcoded `color` (e.g. `#111827` dark text, or white text) that lands on a background of the
 *   same lightness — either the style's own background or, when it sets none, the themed card
 *   surface → we lighten/darken it just enough to clear WCAG AA, keeping its hue.
 * A TRANSLUCENT background (`rgba(…)`, the shape the block prompts ask the agent to emit for row
 * tints) is first composited onto the surface underneath, because the contrast primitives ignore
 * alpha and would otherwise judge the tint by its solid color rather than what it paints as.
 * Left untouched when there is nothing to contrast against (no background and no live theme), when
 * the background isn't a parseable color (e.g. a gradient), or when the `color` itself isn't
 * a parseable literal (`inherit`, `var(…)`, CSS names), so intentional block styling wins.
 */
export function withReadableTextColor(
  style: Record<string, string> | undefined,
  /**
   * Surface behind the style: used as the background when the style paints none of its own, and as
   * the backdrop a translucent background is composited onto. Defaults to the live card.
   */
  fallbackBackground: string | undefined = getThemeCardBackground()
): Record<string, string> | undefined {
  if (!style) {
    return style;
  }
  const background = style.backgroundColor ?? style.background;
  const raw = Color.tryParse(background ?? fallbackBackground ?? "");
  if (!raw) {
    return style;
  }
  // Flatten a TRANSLUCENT background onto the surface underneath before measuring anything.
  // Every contrast primitive here (relativeLuminance, contrastRatio, ensureContrast) reads only RGB
  // and ignores alpha, so a subtle tint like `rgba(239, 68, 68, 0.12)` — the exact shape the block
  // prompts tell the agent to emit for row highlights — scored as SOLID red: luminance 0.23, "dark
  // background", near-white text. On a white card that tint actually paints as light pink, so the
  // row came out white-on-white (~1.1:1) in light mode while looking fine in dark, i.e. white in
  // both modes. Compositing first makes the luminance/contrast branches below see what the user
  // sees. With no resolvable backdrop we can't know the real color, so measure the raw value as
  // before rather than guessing a surface.
  const backdrop = Color.tryParse(fallbackBackground ?? "");
  const parsed = raw.alpha < 1 && backdrop ? raw.compositeOver(backdrop) : raw;
  if (style.color != null) {
    const foreground = Color.tryParse(style.color);
    // Unparseable values (`inherit`, `currentColor`, `var(…)`, CSS color names) can't be
    // contrast-checked, so leave them for the browser to resolve rather than guessing.
    if (!foreground || foreground.contrastRatio(parsed) >= WCAG_AA_NORMAL_TEXT) {
      return style;
    }
    // Nudge lightness rather than jumping to monochrome, so a semantic hue (status red/green)
    // survives the crossing instead of being flattened to near-white/near-black.
    return { ...style, color: ensureContrast(foreground, parsed).toHexString() };
  }
  // No text color at all: only a style-owned background needs one (without it the row/cell
  // correctly inherits the theme foreground, which already contrasts with the card).
  if (!background) {
    return style;
  }
  return {
    ...style,
    color: parsed.relativeLuminance() > 0.5 ? CONTRAST_DARK_TEXT : CONTRAST_LIGHT_TEXT,
  };
}

type LegendItemLike = Record<string, unknown>;
type LegendDatasetLike = { backgroundColor?: unknown; borderColor?: unknown };

/** A Chart.js color option is a single color or one-per-element array; pull the color for `index`. */
function pickColorAt(color: unknown, index: number): string | undefined {
  if (Array.isArray(color)) {
    return typeof color[index] === "string" ? (color[index] as string) : undefined;
  }
  return typeof color === "string" ? color : undefined;
}

/** Near-black / near-white text that passes contrast against any solid background. */
function readableTextOn(background: Color): string {
  return background.relativeLuminance() > 0.5 ? CONTRAST_DARK_TEXT : CONTRAST_LIGHT_TEXT;
}

/**
 * One readable text color for a set of palette-colored surfaces (e.g. treemap cells), chosen
 * against the palette's AVERAGE luminance. Used where per-element label color isn't scriptable, so
 * a single color must serve all cells; picks white for a mostly-dark palette, dark for a light one.
 */
export function readableTextForPalette(palette: readonly string[]): string {
  const luminances = palette
    .map(color => Color.tryParse(color)?.relativeLuminance())
    .filter((n): n is number => n != null);
  const average = luminances.length
    ? luminances.reduce((sum, n) => sum + n, 0) / luminances.length
    : 0;
  return average > 0.5 ? CONTRAST_DARK_TEXT : CONTRAST_LIGHT_TEXT;
}

/**
 * Backward-compat safeguard for custom `legend.labels.generateLabels`. Keeps the legend consistent
 * with the drawn chart on themed dashboards by (1) stamping the theme `legendTextColor` on items
 * without an explicit `fontColor` (else custom items render with Chart.js's dark default and are
 * unreadable on a dark card), and (2) re-syncing each item's `fillStyle`/`strokeStyle` to the
 * RESOLVED dataset color (the color resolver recolors segments/bars to the theme, but a custom
 * generateLabels usually reads the block's pre-resolution colors, so the legend drifts). The drawn
 * segment colors stay the source of truth; unresolvable colors leave the item's swatch untouched.
 */
export function syncLegendItemsToTheme(
  items: Array<LegendItemLike | null | undefined>,
  datasets: LegendDatasetLike[],
  legendTextColor: string | undefined
): Array<LegendItemLike | null | undefined> {
  return items.map(item => {
    if (!item) {
      return item;
    }
    const next: LegendItemLike = { ...item };
    if (next.fontColor == null && legendTextColor) {
      next.fontColor = legendTextColor;
    }
    const datasetIndex = typeof next.datasetIndex === "number" ? next.datasetIndex : 0;
    const dataset = datasets[datasetIndex];
    if (dataset) {
      const elementIndex = typeof next.index === "number" ? next.index : 0;
      const fill = pickColorAt(dataset.backgroundColor, elementIndex);
      const stroke = pickColorAt(dataset.borderColor, elementIndex);
      if (fill != null) {
        next.fillStyle = fill;
      }
      if (stroke != null) {
        next.strokeStyle = stroke;
      }
    }
    return next;
  });
}

const DATALABEL_FILL_CHART_TYPES = new Set([
  "pie",
  "doughnut",
  "funnel",
  "treemap",
  "matrix",
  "polarArea",
]);

type DatalabelContextLike = {
  dataset?: { backgroundColor?: unknown };
  dataIndex?: number;
  [key: string]: unknown;
};

// Per datalabels `anchor`, the `align` values that push the label off the element onto the card.
const OUTWARD_ALIGNS: Record<string, Set<unknown>> = {
  end: new Set(["end", "right", "top"]),
  start: new Set(["start", "left", "bottom"]),
};
// Per `anchor`, the `align` values that draw the label back INTO a bar (on its fill).
const INWARD_ALIGNS: Record<string, Set<unknown>> = {
  end: new Set(["start", "left", "bottom"]),
  start: new Set(["end", "right", "top"]),
};
const BAR_CHART_TYPES = new Set(["bar", "horizontalBar"]);

/**
 * A datalabels option the way the plugin resolves it: scriptable values (the label
 * enforcement pass turns bar `anchor`/`align` into per-label functions) get the label context.
 */
function resolveDatalabelOption(value: unknown, context: DatalabelContextLike): unknown {
  return typeof value === "function" ? value(context, context?.dataIndex) : value;
}

/**
 * Shared decision for hardcoded chart text colors (datalabels, axis ticks, …): keep the block's
 * color when it's readable against `background`, otherwise fall back to `themedFallback` (the
 * theme's designed color for that surface) or, absent that, an auto-contrasting color. Returns the
 * original untouched when the background can't be parsed, so intentional styling is respected.
 */
export function themedChartTextColor(
  originalColor: string,
  background: string | undefined,
  themedFallback?: string
): string {
  const bg = background ? Color.tryParse(background) : null;
  if (!bg) {
    return originalColor;
  }
  const fg = Color.tryParse(originalColor);
  if (fg && fg.contrastRatio(bg) >= WCAG_AA_NORMAL_TEXT) {
    return originalColor;
  }
  return themedFallback ?? readableTextOn(bg);
}

/**
 * Backward-compat safeguard for a hardcoded `plugins.datalabels.color`. Blocks often pin a fixed
 * label color (e.g. "#374151") that is unreadable on a dark card and never adapts. This returns the
 * block's color when it's readable against the surface the label sits on, else a contrasting color.
 * Labels centered on a bar/segment, drawn into a bar from its edge, or on any fill-based chart type
 * contrast against the element's resolved fill; labels anchored outside (e.g. value labels above
 * bars) contrast against the card. `anchor`/`align` may be scriptable and are resolved per label.
 */
export function themedDatalabelColor(
  context: DatalabelContextLike,
  opts: {
    originalColor: string;
    chartType: string | undefined;
    anchor: unknown;
    align: unknown;
    cardBackground: string | undefined;
  }
): string {
  const { originalColor, chartType, cardBackground } = opts;
  // Placement is decided per label (chartLabelEnforcement moves a bar's label inside or outside
  // from its real geometry), so resolve it for THIS label before deciding which surface it's on.
  const anchor = resolveDatalabelOption(opts.anchor, context);
  const align = resolveDatalabelOption(opts.align, context);
  const centered = (anchor == null || anchor === "center") && (align == null || align === "center");
  // An outward label sits on the card even on a fill-type chart (the manual "outside" override on
  // a pie); contrasting it against the slice picked white text that vanished on a light card.
  const pointsOutward = OUTWARD_ALIGNS[String(anchor)]?.has(align) ?? false;
  // A bar label drawn back into the bar from its edge sits on the bar's fill.
  const insideBar =
    chartType != null &&
    BAR_CHART_TYPES.has(chartType) &&
    (INWARD_ALIGNS[String(anchor)]?.has(align) ?? false);
  const onFill =
    centered ||
    insideBar ||
    (chartType != null && DATALABEL_FILL_CHART_TYPES.has(chartType) && !pointsOutward);

  const background = onFill
    ? pickColorAt(context?.dataset?.backgroundColor, context?.dataIndex ?? 0) ?? cardBackground
    : cardBackground;

  return themedChartTextColor(originalColor, background);
}

type DatalabelConfigLike = Record<string, unknown>;

/**
 * Backward-compat safeguard for datalabel text color, applied to the chart-level
 * `plugins.datalabels` AND to every dataset's own `datalabels` (the plugin merges chart-level
 * under dataset-level, so a dataset with its own options renders from its own config). A
 * block that hardcodes a static `color` (e.g. "#374151") is unreadable on a dark card and
 * never adapts; with no color the plugin inherits the themed default, which contrasts the
 * CARD but not necessarily the bar/slice FILL the label sits on. Each config's color is
 * wrapped so it is kept when readable against the surface THAT config's label sits on
 * (its own anchor/align, falling back to the chart-level pair — scriptable placements from
 * the label enforcement pass are resolved per label), else swapped for a contrasting one.
 * Scriptable colors are left alone (the block is already being dynamic); a text stroke
 * (outline) already guarantees contrast, and overriding the fill would blur the glyph.
 */
export function applyDatalabelColorSafeguards(
  chartOptions: ChartOptions,
  opts: { cardBackground: string | undefined; defaultLabelColor: string | undefined }
): void {
  const { cardBackground, defaultLabelColor } = opts;
  const plugins = ((chartOptions.options ||= {}).plugins ||= {}) as Record<string, unknown>;
  const chartLevelRaw = plugins.datalabels;
  const chartLevel =
    chartLevelRaw && typeof chartLevelRaw === "object"
      ? (chartLevelRaw as DatalabelConfigLike)
      : undefined;
  // Captured before the chart-level color is wrapped: datasets without their own color
  // inherit the block's static one, not our wrapper.
  const chartLevelStaticColor =
    typeof chartLevel?.color === "string" ? chartLevel.color : undefined;

  const hasTextStroke = (config: DatalabelConfigLike): boolean =>
    typeof config.textStrokeColor === "string" && (Number(config.textStrokeWidth) || 0) > 0;

  const wrap = (
    target: DatalabelConfigLike,
    merged: DatalabelConfigLike,
    chartType: string | undefined
  ): void => {
    if (hasTextStroke(merged)) {
      return;
    }
    const own = target.color;
    let originalColor: string | undefined;
    if (typeof own === "string") {
      originalColor = own;
    } else if (own === undefined) {
      originalColor =
        target === chartLevel ? defaultLabelColor : chartLevelStaticColor ?? defaultLabelColor;
    }
    if (originalColor === undefined) {
      return; // Scriptable block color, or no themed default to contrast: leave it.
    }
    const anchor = merged.anchor;
    const align = merged.align;
    const color = originalColor;
    target.color = (context: DatalabelContextLike) =>
      themedDatalabelColor(context, {
        originalColor: color,
        chartType,
        anchor,
        align,
        cardBackground,
      });
  };

  if (chartLevel) {
    wrap(chartLevel, chartLevel, chartOptions.type);
  }
  for (const dataset of chartOptions.data?.datasets ?? []) {
    const ownRaw = (dataset as Record<string, unknown>).datalabels;
    if (!ownRaw || typeof ownRaw !== "object") {
      continue;
    }
    const own = ownRaw as DatalabelConfigLike;
    const merged: DatalabelConfigLike = { ...(chartLevel ?? {}), ...own };
    if (merged.display === false) {
      continue;
    }
    const datasetType = (dataset as Record<string, unknown>).type;
    wrap(own, merged, typeof datasetType === "string" ? datasetType : chartOptions.type);
  }
}

const READABLE_TEXT_EXCLUDE_SELECTOR = ".ag-root-wrapper, .chart-container, canvas, svg";
// Text at or above this HSL saturation reads as an intentional (semantic/brand) color and is left
// alone; below it is treated as neutral grayscale text that should follow the theme foreground.
const NEUTRAL_TEXT_MAX_SATURATION = 0.5;
// Neutral text lighter than this maps to the muted foreground token; darker maps to the full one.
const MUTED_TEXT_MIN_LIGHTNESS = 0.4;
// Live themed foreground vars (set on the iframe root by applyTailwindTheme).
const CARD_FOREGROUND_VAR = "var(--theme--color-card-foreground)";
const CARD_MUTED_FOREGROUND_VAR = "var(--theme--color-card-muted-foreground)";

/**
 * Backward-compat safeguard for neutral text in a block's custom HTML. AI-generated blocks hardcode
 * `text-gray-*` / fixed hex on labels and numbers, which is unreadable on a dark card (and, if we
 * baked a fixed color, would be one theme behind — the theme's CSS is applied asynchronously via a
 * view transition, so reading the computed background here is unreliable). Instead we decide from
 * the TEXT color, which comes from a fixed Tailwind class and is theme-independent: near-grayscale
 * text is bound to a LIVE themed foreground CSS variable so it always tracks the current theme.
 * Saturated (semantic/brand) colors, pills/badges (which paint their own background), and
 * chart/table internals are left untouched.
 */
export function enforceReadableCardText(
  root: HTMLElement | null,
  win: (Window & typeof globalThis) | undefined
): void {
  if (!root || !win) {
    return;
  }
  const isTransparent = (color: string | undefined | null): boolean =>
    !color || color === "transparent" || color === "rgba(0, 0, 0, 0)";

  const walker = win.document.createTreeWalker(root, win.NodeFilter.SHOW_ELEMENT, {
    acceptNode: (node: Node) => {
      const el = node as HTMLElement;
      // Skip chart/table internals and everything under them — those own their theming.
      if (el.matches?.(READABLE_TEXT_EXCLUDE_SELECTOR)) {
        return win.NodeFilter.FILTER_REJECT;
      }
      return win.NodeFilter.FILTER_ACCEPT;
    },
  });

  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const el = node as HTMLElement;
    // Only elements that render their own text (a direct non-empty text node).
    const hasOwnText = Array.from(el.childNodes).some(
      child => child.nodeType === 3 && (child.textContent ?? "").trim().length > 0
    );
    if (!hasOwnText) {
      continue;
    }
    const style = win.getComputedStyle(el);
    // Element paints its own background (pill/badge) → its text is meant to contrast THAT, leave it.
    if (!isTransparent(style.backgroundColor)) {
      continue;
    }
    const textColor = Color.tryParse(style.color);
    if (!textColor) {
      continue;
    }
    const { s, l } = textColor.hsl;
    // Saturated → an intentional color the block chose; leave it (generation handles matching).
    if (s >= NEUTRAL_TEXT_MAX_SATURATION) {
      continue;
    }
    el.style.setProperty(
      "color",
      l >= MUTED_TEXT_MIN_LIGHTNESS ? CARD_MUTED_FOREGROUND_VAR : CARD_FOREGROUND_VAR
    );
  }
}

// --------------------------------
// Helper Functions (exposed to render_function_js)
// --------------------------------

const _MONTHS: Record<string, number> = {
  jan: 1,
  feb: 2,
  mar: 3,
  apr: 4,
  may: 5,
  jun: 6,
  jul: 7,
  aug: 8,
  sep: 9,
  oct: 10,
  nov: 11,
  dec: 12,
};

/**
 * Parse a date-like category label ("Jan", "Jan 5", "Feb 12, 2025", "Jan '25",
 * "Q1 25", "Week of Mar 3", "2025-01-05") into a numeric sort key. Returns `NaN`
 * when the label is not date-like.
 *
 * Keys are only ever compared against other keys from the SAME axis (one label
 * style), so the absolute scale does not matter — only the relative ordering does.
 * Exposed so render_function_js can order date axes chronologically instead of
 * alphabetically (the #1 cause of "April shows before January" bugs).
 */
export function parseDateLabelToSortKey(label: string): number {
  if (typeof label !== "string") {
    return NaN;
  }
  const raw = label.trim();
  if (!raw) {
    return NaN;
  }

  // ISO date / month: 2025-01-05, 2025-01.
  if (/^\d{4}-\d{2}(-\d{2})?$/.test(raw)) {
    const ts = Date.parse(raw.length === 7 ? `${raw}-01` : raw);
    return Number.isNaN(ts) ? NaN : ts;
  }

  // Quarter: "Q1 25", "Q1 '25", "Q1 2025".
  const q = raw.match(/^Q([1-4])\D*('?)(\d{2}|\d{4})?$/i);
  if (q) {
    const quarter = Number(q[1]);
    const year = q[3] ? (q[3].length === 2 ? 2000 + Number(q[3]) : Number(q[3])) : 0;
    return year * 10 + quarter;
  }

  // Month-name based, optionally prefixed with "Week of": "Jan", "Jan 5",
  // "January 2025", "Feb 12, 2025", "Jan '25", "Week of Mar 3".
  const body = raw.replace(/^week\s+of\s+/i, "");
  const monthMatch = body.match(/^([A-Za-z]{3,9})\b(.*)$/);
  if (monthMatch) {
    const month = _MONTHS[monthMatch[1].slice(0, 3).toLowerCase()];
    if (month) {
      const rest = monthMatch[2];
      const yearMatch = rest.match(/'(\d{2})\b/) || rest.match(/\b(\d{4})\b/);
      const year = yearMatch
        ? yearMatch[1].length === 2
          ? 2000 + Number(yearMatch[1])
          : Number(yearMatch[1])
        : 0;
      const restNoYear = yearMatch ? rest.replace(yearMatch[0], "") : rest;
      const dayMatch = restNoYear.match(/\b(\d{1,2})\b/);
      const day = dayMatch ? Number(dayMatch[1]) : 0;
      return year * 10000 + month * 100 + day;
    }
  }

  // Last resort: let the JS engine try (handles "Jan 5 2025", full dates, etc.).
  const ts = Date.parse(raw);
  return Number.isNaN(ts) ? NaN : ts;
}

/**
 * Reorder a Chart.js `data` object ({ labels, datasets }) so its date-like category
 * labels run in chronological order. Reorders `labels`, every `dataset.data`, and any
 * per-point array property (e.g. `backgroundColor`) whose length matches the labels.
 *
 * No-op (returns the data unchanged) unless there are >= 2 labels and EVERY label
 * parses as date-like — so intentionally value-sorted or non-date category axes are
 * never scrambled. Mutates and returns the same object for inline use:
 *   const data = { labels, datasets: [{ label: "Revenue", data }] };
 *   createChart(cardElement, { type: "bar", data: sortChartDataByDateLabels(data) });
 */
export function sortChartDataByDateLabels(data: ChartOptions["data"]): ChartOptions["data"] {
  const labels = data?.labels;
  if (!Array.isArray(labels) || labels.length < 2) {
    return data;
  }

  const keys = labels.map(parseDateLabelToSortKey);
  if (keys.some(k => Number.isNaN(k))) {
    return data;
  } // not a date axis — leave as-is.

  const order = labels.map((_, i) => i).sort((a, b) => keys[a] - keys[b]);
  if (order.every((idx, i) => idx === i)) {
    return data;
  } // already chronological.

  const reorder = <T>(arr: T[]): T[] => order.map(i => arr[i]);

  data.labels = reorder(labels);
  for (const dataset of data.datasets ?? []) {
    if (Array.isArray(dataset.data) && dataset.data.length === labels.length) {
      dataset.data = reorder(dataset.data);
    }
    for (const [key, value] of Object.entries(dataset)) {
      if (key !== "data" && Array.isArray(value) && value.length === labels.length) {
        dataset[key] = reorder(value);
      }
    }
  }
  return data;
}

/**
 * Creates a Chart.js chart and returns a cleanup function.
 * This is the React-safe replacement for the imperative createChart function.
 */
/**
 * Recolor `chartOptions.data.datasets` IN PLACE against the resolved theme (Step 1.5 of chart
 * theming). Pure apart from the documented mutation — no DOM, no window — so every branch is
 * unit-testable; `createChart` just calls it with the theme read off the target window.
 *
 * The agent tags each dataset with a `__coeffColor` intent; `resolveDatasetColors` maps that
 * intent to concrete hues against the live theme palette + mode-resolved semantic tokens, so
 * charts re-color when the viewer switches themes (the AI otherwise hardcodes hex that never
 * adapts). Exempt cases keep their deliberate, data-driven colors and never route through the
 * resolver:
 *   - `matrix` (heatmap) — data-driven color scale,
 *   - `choropleth` — region fill comes from the `scales.color` ramp; a dataset backgroundColor
 *     (even fill-when-unset) overrides it with one uniform color (bubbleMap is NOT exempt:
 *     bubbles are uniform-color/size-driven, so theme recoloring is desired),
 *   - scriptable colors (`backgroundColor`/`borderColor` is a function) — heatmap/treemap ramps.
 * The resolver decides HUE only; this function layers the theme's bar/line fill opacity and the
 * transparent-border-when-borderWidth-0 rule on top. A `fixed` intent (user pins / brand colors)
 * resolves to `{ skip: true }`, leaving the dataset's existing colors untouched.
 *
 * Legacy-vs-palette gate: pre-palette dashboards (legacy slug themes) keep the original contract
 * for UNTAGGED datasets — colors are filled only when unset, never overridden — so hand-authored
 * colors (green/red threshold arrays, waterfalls) render as authored (A′ baseline). Palette and
 * custom themes re-theme everything not declared `fixed`, and an explicit intent is always
 * honored regardless of theme model.
 */
export function applyThemeToChartDatasets(chartOptions: ChartOptions, theme: Theme | undefined) {
  const themeConfig = theme?.chartjs;
  const colorPalette = themeConfig?.colorPalette;
  const barBgOpacity = themeConfig?.barBackgroundOpacity ?? 0.2;
  const barBorderWidth = themeConfig?.barBorderWidth ?? 0;
  const lineBgOpacity = themeConfig?.lineBackgroundOpacity ?? 0.2;

  // New theme model (`palette:*` / `custom:*`) gates the single-series-bar default to one primary
  // color; legacy bare-slug themes keep the per-bar rainbow. Resolved once, passed to the resolver.
  const themeId = theme?.themeId;
  const isNewModel = isCustomThemeId(themeId) || (themeId?.startsWith("palette:") ?? false);
  const isDark = theme?.agGrid.params.browserColorScheme === "dark";
  const tokenDefaults = DEFAULT_SEMANTIC_TOKENS[isDark ? "dark" : "light"];
  const tokens: SemanticTokens = {
    positive: themeConfig?.positive ?? tokenDefaults.positive,
    negative: themeConfig?.negative ?? tokenDefaults.negative,
    neutral: themeConfig?.neutral ?? tokenDefaults.neutral,
  };

  const hasScriptableColor = (ds: Record<string, unknown>): boolean =>
    typeof ds.backgroundColor === "function" || typeof ds.borderColor === "function";

  // Layer the theme's fill opacity onto the resolver's hue-only output. `shades` already emits
  // rgba (non-hex) and is passed through untouched; every other color is a hex we convert to rgba.
  const applyBgOpacity = (color: ChartElementColor, opacity: number): ChartElementColor => {
    const withOpacity = (c: string) => (c.startsWith("#") ? hexToRgba(c, opacity) : c);
    return Array.isArray(color) ? color.map(withOpacity) : withOpacity(color);
  };

  if (chartOptions.data?.datasets) {
    const datasets = chartOptions.data.datasets;
    const seriesCount = datasets.length;
    datasets.forEach((dataset, seriesIndex) => {
      // Exempt data-driven coloring: heatmaps, choropleth maps (region fill comes from the
      // scales.color ramp — even a fill-when-unset backgroundColor would override it with one
      // uniform color), and scriptable (value-driven) color functions.
      const isExempt =
        chartOptions.type === "matrix" ||
        chartOptions.type === "choropleth" ||
        hasScriptableColor(dataset);
      // Legacy gate (see docstring): untagged datasets on legacy themes are fill-when-unset only.
      const fillOnlyWhenUnset = !isNewModel && dataset.__coeffColor == null;

      if (colorPalette && !isExempt) {
        const resolved = resolveDatasetColors({
          intent: dataset.__coeffColor as string | undefined,
          palette: colorPalette,
          tokens,
          chartType: chartOptions.type,
          seriesCount,
          seriesIndex,
          dataLength: dataset.data.length,
          isNewModel,
        });

        if (!("skip" in resolved)) {
          const setBg = !fillOnlyWhenUnset || dataset.backgroundColor == null;
          const setBorder = !fillOnlyWhenUnset || dataset.borderColor == null;
          if (
            chartOptions.type === "pie" ||
            chartOptions.type === "doughnut" ||
            chartOptions.type === "funnel"
          ) {
            // One resolved color per segment; these types take no fill-opacity treatment.
            if (setBg) {
              dataset.backgroundColor = resolved.backgroundColor;
            }
            if (setBorder) {
              dataset.borderColor = resolved.borderColor;
            }
          } else if (chartOptions.type === "bar") {
            // Theme plugins (e.g., barGradient) may re-derive backgroundColor during rendering.
            if (setBg) {
              dataset.backgroundColor = applyBgOpacity(resolved.backgroundColor, barBgOpacity);
            }
            if (setBorder) {
              dataset.borderColor = barBorderWidth > 0 ? resolved.borderColor : "transparent";
            }
            if (dataset.borderWidth == null) {
              dataset.borderWidth = barBorderWidth;
            }
          } else if (chartOptions.type === "line") {
            // Line/area charts use line-specific fill opacity.
            // Theme plugins (e.g., lineFillGradient) may re-derive backgroundColor during rendering.
            if (setBg) {
              dataset.backgroundColor = applyBgOpacity(resolved.backgroundColor, lineBgOpacity);
            }
            if (setBorder) {
              dataset.borderColor = resolved.borderColor;
            }
            // Solid point centers (the colored ring comes from pointBorderColor, which defaults to
            // the dataset borderColor): card surface fill instead of the translucent area fill.
            // New-model themes only, and never over a block's own explicit choice.
            if (isNewModel && dataset.pointBackgroundColor == null) {
              dataset.pointBackgroundColor = theme?.tailwind.card.backgroundColor;
            }
          } else {
            // Unknown/other types (sankey, scatter, radar, …): fill only when unset; never override.
            if (dataset.backgroundColor == null) {
              dataset.backgroundColor = applyBgOpacity(resolved.backgroundColor, barBgOpacity);
            }
            if (dataset.borderColor == null) {
              dataset.borderColor = resolved.borderColor;
            }
          }
        }
      }

      // Treemap cells are colored by a scriptable `backgroundColor` (exempt from the resolver
      // above), so recolor them to the theme palette here — unless the block pinned colors with
      // `__coeffColor:'fixed'`, or the dashboard is on a legacy theme with no explicit intent
      // (same legacy gate as above: authored scriptable ramps keep rendering as authored). Border
      // blends into the card surface instead of a stark hardcoded white; labels/captions get one
      // readable color (treemap `labels.color` isn't per-cell scriptable, so we contrast the
      // average palette cell).
      if (
        chartOptions.type === "treemap" &&
        colorPalette &&
        colorPalette.length > 0 &&
        !fillOnlyWhenUnset &&
        dataset.__coeffColor !== "fixed"
      ) {
        const palette = colorPalette;
        dataset.backgroundColor = (ctx: { type?: string; dataIndex?: number }) => {
          if (ctx?.type !== "data") {
            return "transparent";
          }
          const base = Color.tryParse(palette[(ctx.dataIndex ?? 0) % palette.length]);
          return base ? base.withAlpha(0.9).toRgbString() : "transparent";
        };
        const treemapCardBg = theme?.tailwind?.card?.backgroundColor;
        if (treemapCardBg) {
          dataset.borderColor = treemapCardBg;
        }
        const treemapLabelColor = readableTextForPalette(palette);
        const dsLabels = (dataset as { labels?: { color?: unknown } }).labels;
        if (dsLabels && typeof dsLabels === "object") {
          dsLabels.color = treemapLabelColor;
        }
        const dsCaptions = (dataset as { captions?: { color?: unknown } }).captions;
        if (dsCaptions && typeof dsCaptions === "object" && dsCaptions.color != null) {
          dsCaptions.color = treemapLabelColor;
        }
      }

      // Sankey nodes/flows are colored by scriptable `colorFrom`/`colorTo` (which the resolver has
      // no path for), so recolor them to the theme palette here — one stable palette slot per
      // distinct node — unless the block pinned colors with `__coeffColor:'fixed'` or the legacy
      // gate applies. Node names still label the flows, so treating color as categorical (vs the
      // block's hardcoded status hues) is an acceptable trade for theme consistency.
      if (
        chartOptions.type === "sankey" &&
        colorPalette &&
        colorPalette.length > 0 &&
        !fillOnlyWhenUnset &&
        dataset.__coeffColor !== "fixed"
      ) {
        const palette = colorPalette;
        const flows = Array.isArray(dataset.data)
          ? (dataset.data as Array<{ from?: unknown; to?: unknown }>)
          : [];
        const nodeNames = [
          ...new Set(
            flows
              .flatMap(flow => [flow?.from, flow?.to])
              .filter((name): name is string => typeof name === "string")
          ),
        ];
        const nodeColor = (name: unknown): string => {
          const index = typeof name === "string" ? nodeNames.indexOf(name) : -1;
          const base = Color.tryParse(palette[(index < 0 ? 0 : index) % palette.length]);
          return base ? base.toHexString() : palette[0];
        };
        dataset.colorFrom = (ctx: {
          dataset?: { data?: Array<{ from?: unknown }> };
          dataIndex?: number;
        }) => nodeColor(ctx?.dataset?.data?.[ctx?.dataIndex ?? 0]?.from);
        dataset.colorTo = (ctx: {
          dataset?: { data?: Array<{ to?: unknown }> };
          dataIndex?: number;
        }) => nodeColor(ctx?.dataset?.data?.[ctx?.dataIndex ?? 0]?.to);
      }

      // Sankey node labels are chrome that sits on the card surface, but the plugin defaults them
      // to black (it ignores Chart.defaults.color), so they're unreadable on a dark card. Theme the
      // label color to the foreground regardless of __coeffColor (labels aren't the data encoding);
      // keep a block's explicit color only when it's already readable on the card.
      if (chartOptions.type === "sankey") {
        const sankeyForeground = themeConfig?.legendTextColor;
        if (sankeyForeground) {
          const sankeyCardBg = theme?.tailwind?.card?.backgroundColor;
          const existingColor = (dataset as { color?: unknown }).color;
          (dataset as { color?: unknown }).color =
            typeof existingColor === "string"
              ? themedChartTextColor(existingColor, sankeyCardBg, sankeyForeground)
              : sankeyForeground;
        }
      }

      // Strip our declarative intent before constructing the chart — Chart.js may warn on the
      // unknown dataset key. Always deleted, even for exempt/unthemed datasets.
      delete dataset.__coeffColor;
    });
  }
}

export function createChart(cardElement: HTMLElement, chartOptions: ChartOptions): CleanupFunction {
  const targetWindow = getTargetWindow();
  const Chart = targetWindow.Chart;
  if (!Chart) {
    throw new Error("Chart.js not loaded");
  }

  let canvas = cardElement.querySelector(".chart-canvas") as HTMLCanvasElement;

  // If canvas is missing (e.g., render function replaced DOM with "no data" message),
  // recreate the chart structure
  if (!canvas) {
    // Log to the iframe console so console capture can detect it.
    targetWindow.console.log(
      "[createChart] Canvas missing, recreating chart structure for",
      cardElement.id
    );

    // Remove existing chart container if it exists
    const existingChartContainer = cardElement.querySelector(".chart-container");
    if (existingChartContainer) {
      existingChartContainer.remove();
    }

    // Create new chart container and canvas
    const chartContainer = targetWindow.document.createElement("div");
    chartContainer.className = "chart-container flex-1 min-h-0";

    canvas = targetWindow.document.createElement("canvas");
    canvas.className = "chart-canvas w-full h-full";

    chartContainer.appendChild(canvas);
    cardElement.appendChild(chartContainer);
  }

  // Check for and destroy any existing chart on this canvas
  // Chart.js maintains a registry of charts, and we need to clean up before creating a new one
  if (Chart.getChart) {
    const existingChart = Chart.getChart(canvas);
    if (existingChart) {
      existingChart.destroy();
    }
  }

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Could not get 2d context");
  }

  // Manual adjustments layer: persisted `chart_overrides` from the block config, applied
  // over the render JS config (mirrors table_overrides). Runs BEFORE the horizontalBar
  // conversion so a switched-to "horizontalBar" flows through it, and before theming so
  // the color resolver sees the final chart type. The block id is stamped on the card by
  // ChartBlock; modal/derived canvases have no host card and get no overrides.
  const chartOverridesBlockId =
    cardElement.closest<HTMLElement>("[data-coeff-block-id]")?.dataset.coeffBlockId;
  const chartUserOverrides = chartOverridesBlockId
    ? _chartOverridesByBlockId[chartOverridesBlockId]
    : undefined;
  if (chartUserOverrides) {
    applyChartUserOverrides(
      chartOptions,
      chartUserOverrides,
      (targetWindow as unknown as Record<string, unknown>).ChartDataLabels
    );
  }

  // Convert horizontalBar to bar with indexAxis: 'y' for Chart.js v3+ compatibility
  if (chartOptions.type === "horizontalBar") {
    chartOptions.type = "bar";
    (chartOptions.options ||= {}).indexAxis = "y";
  }

  // Apply the dashboard theme to chart datasets (extracted pure Step 1.5, see its docstring).
  const theme = targetWindow.__coeffWebDashboardTheme;
  const themeConfig = theme?.chartjs;
  applyThemeToChartDatasets(chartOptions, theme);

  // Chart.js discards onClick's return value, and the documented drill-down pattern is
  // `return createModalDataTable(...)` from inside it. Keep that cleanup and run it when the
  // chart goes away, so a re-render no longer leaks the drawer's grid.
  let closeChartDrilldown: CleanupFunction | null = null;
  type ClickHandler = ((...a: unknown[]) => unknown) & { __coeffOriginalOnClick?: ClickHandler };
  const currentOnClick = chartOptions.options?.onClick as ClickHandler | undefined;
  if (typeof currentOnClick === "function") {
    // Wrap the ORIGINAL handler: a render that reuses one options object across renders must
    // not wrap the previous wrapper, whose cleanup would land in a dead closure.
    const userOnClick = currentOnClick.__coeffOriginalOnClick ?? currentOnClick;
    const wrapped: ClickHandler = async (...args: unknown[]) => {
      const result = await userOnClick(...args);
      if (typeof result === "function") {
        closeChartDrilldown?.();
        closeChartDrilldown = result as CleanupFunction;
      }
    };
    wrapped.__coeffOriginalOnClick = userOnClick;
    (chartOptions.options as Record<string, unknown>).onClick = wrapped;
  }

  // Add pointer cursor on hover for clickable elements
  if (chartOptions.options?.onClick) {
    chartOptions.options.onHover = (
      event: { native: { target: HTMLElement } },
      elements: unknown[]
    ) => {
      if (elements.length > 0) {
        event.native.target.style.cursor = "pointer";
      } else {
        event.native.target.style.cursor = "default";
      }
    };
  }

  // Disable legend click toggle behavior.
  const chartOptionsRecord = (chartOptions.options ||= {});
  const chartPlugins = (chartOptionsRecord.plugins ||= {}) as Record<string, unknown>;
  const legendOptions = (chartPlugins.legend ||= {}) as Record<string, unknown>;
  legendOptions.onClick = () => {};

  // Legend consistency safeguard (backward-compat). A block that supplies a custom `generateLabels`
  // builds its own LegendItems, which causes two problems on themed dashboards:
  //   1. Text color: custom items render with Chart.js's default dark font color and become
  //      unreadable on a dark card — so stamp the themed `legendTextColor` on items missing one.
  //   2. Swatch color: the color resolver above recolors pie/doughnut/bar dataset colors to the
  //      theme, but a custom `generateLabels` typically reads the block's ORIGINAL (pre-resolution)
  //      colors, making the legend disagree with the drawn segments. Re-sync each item's
  //      fill/stroke to the RESOLVED dataset color so the legend always matches what's rendered.
  // Both only fill gaps / correct drift — the drawn segment colors remain the source of truth.
  const legendTextColor = themeConfig?.legendTextColor;
  const legendLabels = (legendOptions.labels ||= {}) as Record<string, unknown>;
  if (legendTextColor && legendLabels.color == null) {
    legendLabels.color = legendTextColor;
  }
  // Wrap only a block-provided generateLabels so its legend item text is END-truncated.
  const userGenerateLabels = legendLabels.generateLabels;
  type LegendChartLike = {
    config?: { type?: string };
    data?: { datasets?: Array<{ backgroundColor?: unknown; borderColor?: unknown }> };
  };
  if (typeof userGenerateLabels === "function") {
    legendLabels.generateLabels = function (this: unknown, chart: LegendChartLike) {
      const items =
        (userGenerateLabels.call(this, chart) as Array<Record<string, unknown>> | undefined) ?? [];
      return truncateLegendItems(
        syncLegendItemsToTheme(items, chart?.data?.datasets ?? [], legendTextColor)
      );
    };
  }

  // Central label enforcement pass (see chartLabelEnforcement.ts): collision-aware data
  // labels, clamp-inside-chart-area, ".00" stripping, category tick end-truncation, and
  // the legend size cap (proportional to the measured chart container).
  const chartContainer = canvas.parentElement;
  const containerWidth = chartContainer?.clientWidth ?? 0;
  const containerHeight = chartContainer?.clientHeight ?? 0;
  // The canvas measurer sizes the room reserved for outside value labels to the real text.
  const measureText = (text: string, font: string): number => {
    ctx.save();
    ctx.font = font;
    const width = ctx.measureText(text).width;
    ctx.restore();
    return width;
  };
  applyChartLabelEnforcement(
    chartOptions,
    containerWidth > 0 && containerHeight > 0
      ? { width: containerWidth, height: containerHeight, measureText }
      : undefined
  );

  // Datalabels text-color safeguard (backward-compat): see applyDatalabelColorSafeguards.
  const cardBackground = theme?.tailwind?.card?.backgroundColor;
  applyDatalabelColorSafeguards(chartOptions, {
    cardBackground,
    defaultLabelColor: themeConfig?.defaultColor,
  });

  // Axis tick + grid color safeguard (backward-compat). Blocks hardcode `scales.<axis>.ticks.color`
  // (e.g. "#374151"/"#6b7280") and `scales.<axis>.grid.color` (e.g. "rgba(0,0,0,0.06)"), overriding
  // the themed defaults → unreadable ticks / invisible gridlines on a dark card.
  //   - Ticks sit on the card surface: keep the block's color when readable, else theme `axisTickColor`.
  //   - Gridlines are pure chrome: always replace a static color with the theme `gridColor`.
  // Scriptable colors are left alone.
  const axisTickColor = themeConfig?.axisTickColor;
  const gridColor = themeConfig?.gridColor;
  const scales = (chartOptions.options as any).scales;
  if (scales && typeof scales === "object") {
    Object.values(scales).forEach((axis: any) => {
      const ticks = axis?.ticks;
      if (ticks && typeof ticks.color === "string") {
        ticks.color = themedChartTextColor(ticks.color, cardBackground, axisTickColor);
      }
      const grid = axis?.grid;
      if (grid && typeof grid.color === "string" && gridColor) {
        grid.color = gridColor;
      }
    });
  }

  // Add theme plugins to chart config
  const themePlugins = themeConfig?.plugins ?? [];
  if (themePlugins.length > 0) {
    chartOptions.plugins = [...(chartOptions.plugins ?? []), ...themePlugins];
  }

  // Every chart is born with an inline plugins array, even an empty one, so anything layering
  // on a per-chart plugin can push into it rather than assign. Cheap to keep: Chart.js wraps
  // the config in a Config class whose `plugins` is a getter with NO setter, so a would-be
  // assignment on an already-created chart throws.
  chartOptions.plugins = chartOptions.plugins || [];

  const chart = new Chart(ctx, adoptChartRealm(chartOptions, targetWindow));

  return () => {
    closeChartDrilldown?.();
    chart.destroy();
  };
}

function isNumericOrDateValue(value: unknown): boolean {
  return typeof value === "number" || value instanceof Date;
}

/**
 * True when a raw cell value is a date-like *label string* (e.g. "Jul 21, 2025",
 * "2025-07", "Q1 25", "Week of Mar 3") — the kind of value blocks emit for a
 * period/week/month column. Bare numbers ("2025", "10") are excluded so numeric
 * columns aren't mistaken for dates: AG Grid already sorts those numerically, and
 * `parseDateLabelToSortKey`'s greedy `Date.parse` fallback would otherwise coerce
 * some of them. Numbers and Date objects are excluded too — they sort correctly
 * under AG Grid's default comparator, so they don't need the label-based one.
 */
function isDateLikeLabel(value: unknown): boolean {
  if (typeof value !== "string") {
    return false;
  }
  const s = value.trim();
  if (!s || /^-?\d+(\.\d+)?$/.test(s)) {
    return false;
  }
  return !Number.isNaN(parseDateLabelToSortKey(s));
}

/**
 * Detects a column whose values are date-like label strings so it can be sorted
 * chronologically instead of alphabetically (the "Aug before Jan" bug — a
 * `.toLocaleDateString` label sorts lexicographically by default). Mirrors the
 * `isNumericOrDateColumn` sampling: checks the first 30 rows and requires EVERY
 * non-empty value to be a date-like label (and at least one to exist), so a column
 * that only sometimes looks date-like — or is really numeric/text — is left on the
 * default comparator.
 */
export function isDateLikeColumn(data: unknown[][], columnIndex: number): boolean {
  let sawValue = false;
  for (const row of data.slice(0, 30)) {
    const value = row[columnIndex];
    if (value == null || value === "") {
      continue;
    }
    if (!isDateLikeLabel(value)) {
      return false;
    }
    sawValue = true;
  }
  return sawValue;
}

/**
 * AG Grid comparator that orders date-like label strings chronologically via
 * `parseDateLabelToSortKey`. Non-date values (blanks, the pinned Total row's label)
 * get a NaN key and are grouped ahead of dated rows in ascending order — AG Grid
 * inverts this for descending.
 */
export function dateLabelComparator(valueA: unknown, valueB: unknown): number {
  const ka = isDateLikeLabel(valueA) ? parseDateLabelToSortKey(valueA as string) : NaN;
  const kb = isDateLikeLabel(valueB) ? parseDateLabelToSortKey(valueB as string) : NaN;
  const aNaN = Number.isNaN(ka);
  const bNaN = Number.isNaN(kb);
  if (aNaN && bNaN) {
    return 0;
  }
  if (aNaN) {
    return -1;
  }
  if (bNaN) {
    return 1;
  }
  return ka - kb;
}

/** True for cells rendered in the pinned "Total" row at the bottom of the grid. */
function isPinnedBottomRow(params: CellRendererParams): boolean {
  return params?.node?.rowPinned === "bottom";
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** Only allow http(s) links — never javascript:/data: URLs injected into href. */
function isSafeHttpUrl(url: string): boolean {
  return /^https?:\/\//i.test(url.trim());
}

/**
 * Builds an AG Grid cellRenderer for the `link` column option: the value renders as an
 * underlined link, with a small external-link icon that additionally fades in on hover (see the
 * `.coeff-cell-link` CSS in WebDashboardContent). Falls back to plain text when the row has no
 * (or an unsafe) URL, so a column where only some rows link stays quiet on the rest.
 *
 * The underline is the at-rest affordance (ENG-8483). It used to be the icon alone, which sat at
 * `opacity: 0` until the cell was hovered — so a linked column was indistinguishable from a plain
 * one, invisible on touch, and invisible in every screenshot path. Users could not tell a link
 * was there and re-asked for links they already had.
 *
 * ONE anchor wraps both parts. Nesting an anchor in an anchor is invalid HTML, and two siblings
 * sharing the href would give every cell two tab stops. The text carries its own span because
 * `text-decoration` inherits into descendants and cannot be cancelled by one, so underlining the
 * anchor would underline the icon glyph too.
 */
function buildLinkCellRenderer(
  hrefFor: (params: CellRendererParams) => string | null | undefined,
  formatValue: (value: unknown) => string
): (params: CellRendererParams) => string {
  return (params: CellRendererParams) => {
    let text: string;
    try {
      text = escapeHtml(String(formatValue(params.value) ?? ""));
    } catch {
      text = escapeHtml(String(params.value ?? ""));
    }
    let href: string | null | undefined;
    try {
      href = hrefFor(params);
    } catch {
      href = null;
    }
    if (!href || !isSafeHttpUrl(href)) {
      return text;
    }
    const safeHref = escapeHtml(href.trim());
    // `aria-hidden` on the icon because the Material Icons glyph is the literal text
    // "open_in_new" in the DOM: without it the link's accessible name reads
    // "Acme Corporation open_in_new". The value text is the name now, which is why the old
    // aria-label="Open link" on the icon anchor is gone.
    return (
      `<a class="coeff-cell-link" href="${safeHref}" target="_blank" rel="noopener noreferrer">` +
      `<span class="coeff-cell-link-text">${text}</span>` +
      `<span class="coeff-cell-link-icon material-icons" aria-hidden="true">open_in_new</span>` +
      `</a>`
    );
  };
}

/**
 * Wraps an agent-supplied `cellRenderer` so a nullish return means "no override —
 * render this cell normally" rather than "render nothing".
 *
 * AG Grid's function-renderer adapter turns a nullish return into an empty
 * `<span>`, so `cellRenderer: (p) => isTotalsRow(p) ? '' : null` silently blanked
 * every data row in the column — the `formatValue` that used to display it became
 * dead code (ENG-8485). Nullish is the natural way to write "no override" because
 * that is exactly what the neighbouring `cellStyle` callback means by `{}`, so the
 * runtime now matches the intuition.
 *
 * Only `null`/`undefined` fall back. `''`, `0`, `false` and DOM elements are
 * returned untouched — a truthiness check here would swallow the deliberate
 * "render an empty cell" case. A throwing renderer degrades to the same fallback,
 * matching valueFormatter/cellStyle/tooltipValueGetter below.
 *
 * The fallback text is HTML-escaped: AG Grid injects a returned string via
 * innerHTML, so raw cell data containing `<` or `&` would otherwise become markup.
 */
export function wrapCellRendererForAgGrid(
  userRenderer: (params: CellRendererParams) => string | null | undefined,
  formatValue?: (value: unknown) => string
): (params: CellRendererParams) => unknown {
  return (params: CellRendererParams) => {
    let result: unknown;
    try {
      result = userRenderer(params);
    } catch {
      return defaultCellHtml(params?.value, formatValue);
    }
    if (result == null) {
      return defaultCellHtml(params?.value, formatValue);
    }
    return result;
  };
}

/**
 * Two separate places need to answer the same question — "what would this cell have shown if
 * nothing overrode it?" — the wrapper that catches a misbehaving `cellRenderer` and falls back,
 * and the pass-through renderer installed for always-show tooltips. Sharing one implementation
 * keeps those two fallbacks from drifting apart.
 *
 * Produces the column's `formatValue(value)` output, or the raw value when the column has no
 * formatter. A `formatValue` that throws degrades to the raw value rather than blanking the cell.
 *
 * The result is HTML-escaped because both callers hand it straight back to AG Grid as a
 * `cellRenderer` return value, and AG Grid injects a returned string with innerHTML — so cell
 * data containing `<` or `&` would otherwise be parsed as markup.
 */
function defaultCellHtml(value: unknown, formatValue?: (value: unknown) => string): string {
  if (!formatValue) {
    return escapeHtml(String(value ?? ""));
  }
  try {
    return escapeHtml(String(formatValue(value) ?? ""));
  } catch {
    return escapeHtml(String(value ?? ""));
  }
}

/**
 * One table can hold columns that want opposite tooltip behavior: most should stay quiet unless
 * their text is clipped, while a column with an explicit `tooltip`, a pill, or a `link` has to
 * show on every hover. AG Grid only exposes `tooltipShowMode` at the *grid* level, so honoring
 * one such column by flipping the grid to "standard" would un-gate every other column in the
 * table, and every header with them.
 *
 * This is the per-column escape hatch. It returns a `cellRenderer` that renders exactly what the
 * cell would have rendered anyway — the column's `formatValue` output, or the raw value — so
 * nothing about the display changes. Installing it simply makes the column *count* as a renderer
 * column, and renderer columns are the ones AG Grid leaves out of truncation measurement.
 *
 * That exemption is the entire mechanism, and it comes from AG Grid's cell-tooltip wiring in
 * `tooltipSvc.enableCellTooltipFeature`:
 *
 *     if (isTooltipWhenTruncated && !ctrl.isCellRenderer()) { ...measure the cell... }
 *     else { shouldDisplayTooltip = () => !isEditing }
 *
 * where `isCellRenderer()` is no more than `colDef.cellRenderer != null ||
 * colDef.cellRendererSelector != null`. A renderer column therefore skips the measurement and
 * always shows, while its neighbours in the same grid stay gated — which is what lets
 * `tooltipShowMode` stay pinned to "whenTruncated" for every table.
 *
 * Two costs, both accepted: the column renders through a cell component rather than plain text
 * (`isCellRenderer()` selects that path too), and its text reaches the DOM via innerHTML, which
 * is why `defaultCellHtml` escapes. Visual truncation is untouched — AG Grid puts `ag-cell-value`
 * on the cell whether or not it has a renderer, and the ellipsis rule lives on that class.
 *
 * The caveat: this leans on an AG Grid internal rather than a documented option. Pill and `link`
 * columns already depend on that same internal for their always-show behavior, so it adds no new
 * exposure — but an AG Grid upgrade that started measuring renderer DOM would silently re-gate
 * these columns. Unit tests cannot see that, so pair a version bump with a manual hover check on
 * a `tooltip: true` column. dashboardHelpers.tooltipMode.test.ts covers the half that is
 * testable: that the pass-through stays display-identical to a default cell, and safe on the
 * innerHTML path.
 */
export function buildDefaultCellRenderer(
  formatValue?: (value: unknown) => string
): (params: CellRendererParams) => string {
  return (params: CellRendererParams) => defaultCellHtml(params?.value, formatValue);
}

/**
 * A lone dash cell (—, –, -) the agent emits as a "nothing to total here"
 * placeholder. The framework blanks these in the total row so empty summary
 * cells read as empty rather than as a column of dashes.
 */
function toFiniteNumber(value: unknown): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function sumColumn(rows: unknown[][], columnIndex: number): number | null {
  let sum = 0;
  let numericCells = 0;
  for (const row of rows) {
    const cell = toFiniteNumber(row[columnIndex]);
    if (cell != null) {
      sum += cell;
      numericCells++;
    }
  }
  return numericCells > 0 ? sum : null;
}

/**
 * Fallback for render JS that passes a static `pinnedBottomRowData` (the
 * pre-`computePinnedBottomRowData` pattern in thousands of existing dashboards):
 * infer which pinned cells are plain column sums by checking them against the
 * unfiltered data. Only those cells are re-summed when the grid is filtered;
 * labels and derived values (averages, margin %) are left untouched since their
 * formula is unknown. Tolerance scales with magnitude (0.1%, floored at half a
 * cent, capped at half a unit) so currency rounding in the JS still matches while
 * small-magnitude columns (rates, ratios) can't mistake an average for a sum.
 */
export function inferSumColumnIndexes(staticRows: unknown[][], data: unknown[][]): Set<number> {
  const sumColumns = new Set<number>();
  const firstRow = staticRows[0];
  if (!firstRow || data.length === 0) {
    return sumColumns;
  }
  firstRow.forEach((pinnedCell, columnIndex) => {
    const pinned = toFiniteNumber(pinnedCell);
    if (pinned == null) {
      return;
    }
    const sum = sumColumn(data, columnIndex);
    if (sum == null) {
      return;
    }
    const tolerance = Math.min(0.5, Math.max(0.005, Math.abs(sum) * 1e-3));
    if (Math.abs(sum - pinned) <= tolerance) {
      sumColumns.add(columnIndex);
    }
  });
  return sumColumns;
}

export function reSumStaticPinnedRows(
  staticRows: unknown[][],
  sumColumns: Set<number>,
  filteredRows: unknown[][]
): unknown[][] {
  return staticRows.map((row, rowIndex) =>
    rowIndex === 0
      ? row.map((cell, columnIndex) =>
          sumColumns.has(columnIndex) ? sumColumn(filteredRows, columnIndex) ?? 0 : cell
        )
      : row
  );
}

export type PinnedRowsComputer = (filteredRows: unknown[][]) => unknown[][];

/**
 * Picks how a table's pinned rows respond to in-grid filtering: the render JS
 * callback when it supplied a function, otherwise inferred column sums over a
 * static row, otherwise nothing (static row shown as-is). Inferred sums only
 * replace the static row while a filter is active — with every row visible
 * (same count ⇒ same set) the author's exact values are shown.
 */
export function buildPinnedRowsComputer(
  tableOptions: Pick<
    DataTableOptions,
    "data" | "pinnedBottomRowData" | "computePinnedBottomRowData"
  >
): PinnedRowsComputer | undefined {
  if (typeof tableOptions.computePinnedBottomRowData === "function") {
    return tableOptions.computePinnedBottomRowData;
  }
  const staticRows = tableOptions.pinnedBottomRowData;
  if (!Array.isArray(staticRows)) {
    return undefined;
  }
  const sumColumns = inferSumColumnIndexes(staticRows, tableOptions.data);
  if (sumColumns.size === 0) {
    return undefined;
  }
  return filteredRows =>
    filteredRows.length === tableOptions.data.length
      ? staticRows
      : reSumStaticPinnedRows(staticRows, sumColumns, filteredRows);
}

function isDashPlaceholder(value: unknown): boolean {
  return typeof value === "string" && /^\s*[-–—]\s*$/.test(value);
}

type DataTableSizingGridApi = {
  getSizesForCurrentTheme: () => { rowHeight: number; headerHeight: number };
  getDisplayedRowCount?: () => number;
  paginationGetRowCount?: () => number;
  isDestroyed?: () => boolean;
};

/**
 * The nearest scrollable ancestor that caps its own height (has a resolved `max-height`). For the
 * drill-down bottom drawer this is the `max-h-[85vh] overflow-y-auto` container; in-dashboard data
 * table blocks have no such ancestor and return null.
 */
function findMaxHeightScrollAncestor(el: HTMLElement): HTMLElement | null {
  let node = el.parentElement;
  while (node) {
    const cs = getComputedStyle(node);
    if (cs.overflowY === "auto" || cs.overflowY === "scroll") {
      const maxH = parseFloat(cs.maxHeight);
      if (Number.isFinite(maxH) && maxH > 0) {
        return node;
      }
    }
    node = node.parentElement;
  }
  return null;
}

/**
 * The visible height budget the drill-down drawer can actually give the grid:
 * the scroller's max-height minus its bottom padding, anything rendered above
 * the grid (the title), and anything reserved below it (the "Load more"
 * footer). Measured at scrollTop 0, so it's independent of the grid's own
 * (possibly stale) height. 2px guard so rounding can't force the scroller to
 * scroll. Returns null when there is no measurable budget — no max-height
 * ancestor, or the drawer is hidden/mid-animation and measures to nonsense
 * (zero rects); callers must NOT treat null as "unlimited" beyond the viewport
 * cap they already apply.
 */
function measureDrawerBudget(gridContainer: HTMLElement, bottomReservePx: number): number | null {
  const scroller = findMaxHeightScrollAncestor(gridContainer);
  if (!scroller) {
    return null;
  }
  const cs = getComputedStyle(scroller);
  const maxH = parseFloat(cs.maxHeight);
  const padBottom = parseFloat(cs.paddingBottom) || 0;
  const scrollerRect = scroller.getBoundingClientRect();
  const gridRect = gridContainer.getBoundingClientRect();
  const available =
    maxH - padBottom - (gridRect.top - scrollerRect.top + scroller.scrollTop) - bottomReservePx - 2;
  return Number.isFinite(available) && available > 0 ? available : null;
}

/**
 * Sizes the AG Grid container under infinite scroll (no pagination). This is the
 * measurement shell: it reads what AG Grid actually rendered — header,
 * currently-displayed data rows (post quick-filter), pinned "Total" row,
 * horizontal scrollbar — plus the space constraint (block container in "fill"
 * mode, viewport cap + drawer budget in "cap" mode), and hands everything to
 * resolveTableGridHeight. ALL sizing rules (fallbacks, clamping, row-boundary
 * snapping, min-height) live in that pure resolver — add new rules there, not
 * here. Returns the resolved pair: the applied grid height plus the natural
 * content height, both consumed by the layout report in applySizing.
 */
function sizeDataTableGrid(
  gridContainer: HTMLElement,
  dataTableContainer: HTMLElement,
  gridApi: DataTableSizingGridApi,
  pinnedBottomRowCount: number,
  sizing:
    | { mode: "fill"; footerHeight: number }
    | { mode: "cap"; maxHeight: number; bottomReservePx?: number }
): ResolvedTableGridHeight {
  const { rowHeight, headerHeight } = gridApi.getSizesForCurrentTheme();
  const dataRowCount = gridApi.getDisplayedRowCount?.() ?? 0;

  const measureHeight = (root: HTMLElement, selector: string): number => {
    const el = root.querySelector<HTMLElement>(selector);
    return el ? el.getBoundingClientRect().height : 0;
  };

  const centerViewport = gridContainer.querySelector<HTMLElement>(".ag-center-cols-viewport");
  const hasHorizontalScroll = centerViewport
    ? centerViewport.scrollWidth > centerViewport.clientWidth + 1
    : false;

  // Report the scrollbar's overlay-ness as a DOM fact (AG Grid marks its fake
  // scrollbar ag-scrollbar-invisible when the OS uses overlay scrollbars);
  // whether an overlay reserves a height lane is a sizing rule owned by
  // resolveTableGridHeight.
  const horizontalScrollEl = gridContainer.querySelector<HTMLElement>(".ag-body-horizontal-scroll");

  const resolved = resolveTableGridHeight(
    {
      measuredHeaderHeight: measureHeight(gridContainer, ".ag-header"),
      themeHeaderHeight: headerHeight,
      themeRowHeight: rowHeight,
      dataRowCount,
      measuredRowsHeight:
        dataRowCount > 0 ? measureHeight(gridContainer, ".ag-center-cols-container") : 0,
      pinnedBottomRowCount,
      measuredPinnedBottomHeight:
        pinnedBottomRowCount > 0 ? measureHeight(gridContainer, ".ag-floating-bottom") : 0,
      horizontalScrollHeight:
        hasHorizontalScroll && horizontalScrollEl
          ? horizontalScrollEl.getBoundingClientRect().height
          : 0,
      horizontalScrollIsOverlay:
        horizontalScrollEl?.classList.contains("ag-scrollbar-invisible") ?? false,
    },
    sizing.mode === "cap"
      ? {
          mode: "cap",
          maxHeight: sizing.maxHeight,
          drawerBudget: measureDrawerBudget(gridContainer, sizing.bottomReservePx ?? 0),
        }
      : {
          mode: "fill",
          availableHeight: Math.max(0, dataTableContainer.clientHeight - sizing.footerHeight),
        }
  );

  // Skip the no-op write: assigning the same height still fires AG Grid's
  // onGridSizeChanged, whose handler re-runs sizing — a wasted full pass per
  // ResizeObserver frame while the height is stable.
  const nextHeight = `${resolved.gridHeight}px`;
  if (gridContainer.style.height !== nextHeight) {
    gridContainer.style.height = nextHeight;
  }
  return resolved;
}

/**
 * Parse selected cells from the AG Grid DOM for clipboard operations.
 * Returns a 2D array of cell values preserving table structure.
 */
function parseSelectedCells(gridContainer: HTMLElement, selection: Selection): string[][] {
  const rows: string[][] = [];

  // Get all AG Grid row elements
  const rowElements = Array.from(gridContainer.querySelectorAll(".ag-row"));

  for (const rowEl of rowElements) {
    // Check if row intersects with selection
    if (!selection.containsNode(rowEl, true)) {
      continue;
    }

    const rowCells: string[] = [];
    // Get cells in DOM order (which matches visual order due to ensureDomOrder: true)
    const cellElements = Array.from(rowEl.querySelectorAll(".ag-cell"));

    for (const cellEl of cellElements) {
      if (selection.containsNode(cellEl, true)) {
        rowCells.push(cellEl.textContent?.trim() ?? "");
      }
    }

    if (rowCells.length > 0) {
      rows.push(rowCells);
    }
  }

  return rows;
}

function sanitizeFileNamePart(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) {
    return "table";
  }
  // Avoid filesystem-reserved characters and collapse whitespace.
  return trimmed
    .replace(/[\/\\?%*:|"<>]/g, "-")
    .replace(/\s+/g, " ")
    .slice(0, 80);
}

function formatCellForExport(value: unknown): string {
  if (value == null) {
    return "";
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function escapeForTsv(value: string): string {
  // Keep paste-friendly single-line cells.
  return value.replace(/\t/g, " ").replace(/\r?\n/g, " ");
}

function escapeForCsv(value: string): string {
  const needsQuotes = /[",\r\n]/.test(value);
  const escaped = value.replace(/"/g, '""');
  return needsQuotes ? `"${escaped}"` : escaped;
}

function downloadTextFile(
  targetWindow: Window,
  targetDocument: Document,
  filename: string,
  content: string,
  mimeType: string
): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = targetDocument.createElement("a");
  a.href = url;
  a.download = filename;
  targetDocument.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Creates a debounced version of a function that delays execution until after
 * the specified delay has passed since the last invocation.
 * @param func The function to debounce
 * @param delay Delay in milliseconds
 * @returns A debounced function with a cancel method
 */
function debounce<T extends (...args: any[]) => void>(
  func: T,
  delay: number
): T & { cancel: () => void } {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const debounced = ((...args: Parameters<T>) => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      func(...args);
      timeoutId = null;
    }, delay);
  }) as T & { cancel: () => void };

  debounced.cancel = () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  return debounced;
}

// Below this width, sizeColumnsToFit() would forcibly compress every column to fit a ~330px
// mobile card, rendering data illegible. Instead let the grid keep its natural column widths and
// scroll horizontally inside the card (data-table-container is overflow-auto).
const AG_GRID_SIZE_TO_FIT_MIN_WIDTH = 640;

// The viewport width above is only a PROXY for how much room the grid itself has, and the
// drill-down drawer breaks that proxy: it spans the full viewport rather than sitting inside a
// padded card. So on a narrow-but-not-phone window — or after the chat sidebar's reserved width is
// released when the viewport crosses below 640px — the drawer's grid has plenty of room, yet the
// viewport check suppressed the re-fit and left columns at widths computed for the previous,
// narrower container, leaving dead space to their right.
//
// Observed container widths: phone in-dashboard card ~323px, phone drill-down drawer ~362px,
// desktop card with the chat sidebar open ~377px, 582px-viewport drawer ~554px. A 480px floor
// admits only the last of those, so phone and desktop behaviour are both unchanged — this is
// purely additive to the viewport rule.
const AG_GRID_SIZE_TO_FIT_MIN_CONTAINER_WIDTH = 480;

// Exported for tests: this runs only inside a live AG Grid, which a unit test cannot stand up.
export function sizeColumnsToFitIfWideEnough(
  gridApi: AgGridGlobals.GridApi,
  targetWindow: DashboardWindow,
  gridContainerEl?: HTMLElement | null
): void {
  // Debounced resize/timeout callbacks can fire after a slicer or theme change
  // destroyed this grid instance; AG Grid throws on API calls after destroy. This
  // has to precede the width checks — clientWidth on a destroyed grid's container
  // is meaningless, and sizeColumnsToFit() would throw regardless of the result.
  // Optional call to match the isDestroyed?: declaration on the local GridApi type
  // and the other call site in this file.
  if (gridApi.isDestroyed?.()) {
    return;
  }
  const containerWidth = gridContainerEl?.clientWidth ?? 0;
  if (
    targetWindow.innerWidth >= AG_GRID_SIZE_TO_FIT_MIN_WIDTH ||
    containerWidth >= AG_GRID_SIZE_TO_FIT_MIN_CONTAINER_WIDTH
  ) {
    // Floor the auto-fit at the build width, not the lower drag-resize floor.
    // sizeColumnsToFit() with no params uses each column's own minWidth
    // (defaultColDef.minWidth = DATA_TABLE_MIN_RESIZE_WIDTH = 48), so without
    // defaultMinWidth here every refit — window/container resize, theme/slicer
    // change, refresh — would crush unpinned columns to 48. autoSizeStrategy's
    // defaultMinWidth only applies to the one-shot initial layout, not these
    // imperative refits. Passing it explicitly mirrors that build floor so
    // fit(140) and drag(48) stay decoupled: drag-resize never routes through
    // sizeColumnsToFit, so the 48 floor on defaultColDef still governs manual drags.
    gridApi.sizeColumnsToFit({ defaultMinWidth: DATA_TABLE_MIN_COLUMN_WIDTH });
  }
}

/**
 * Reorders column definitions to a user-dragged display order (by `field`).
 * Columns missing from the saved order — e.g. a column the agent added after the
 * user last dragged — keep their original index instead of being dumped at the
 * far right; saved fields that no longer exist are ignored. Exported for tests.
 */
export function applyColumnOrder<T extends { field?: string }>(
  defs: T[],
  order: string[] | null | undefined
): T[] {
  if (!order || order.length === 0) {
    return defs;
  }
  const byField = new Map(defs.filter(def => def.field).map(def => [def.field, def]));
  const ordered = order.filter(f => byField.has(f)).map(f => byField.get(f) as T);
  if (ordered.length === 0) {
    return defs;
  }
  const placed = new Set<string | undefined>(order);
  const result = [...ordered];
  defs.forEach((def, originalIndex) => {
    if (!def.field || !placed.has(def.field)) {
      result.splice(Math.min(originalIndex, result.length), 0, def);
    }
  });
  return result;
}

/**
 * Drill-down text reveal (ENG-7527).
 *
 * Drill-down cells stay on one ellipsized line (the .data-table-modal rules in
 * WebDashboardContent), so widening a column is not a reliable way to read a long
 * text field - and wrapping is not an option, because the drawer sizing snaps to a
 * whole number of uniform rows (see snapClampedGridHeightToRowBoundary). Instead a
 * clipped cell reveals its RAW value in an interactive hover/tap panel with a Copy
 * button. Reading the raw value also rescues dashboards already saved with a
 * destructively truncating formatValue, where the clipped characters are gone from
 * the formatted text for good.
 */
/** Long enough to move the pointer off the cell and into the panel to hit Copy. */
const CELL_REVEAL_HIDE_DELAY_MS = 2000;
/**
 * The same journey on touch, where nothing cancels the timer.
 *
 * AG Grid arms the hide timeout the instant the tooltip is shown, unconditionally — see
 * `startHideTimeout()` immediately after its `tooltipShow` dispatch — and on a pointer device
 * hovering the panel is what keeps resetting it. A finger cannot hover, so the desktop 2s would
 * pull the panel away mid-read, or mid-reach for Copy. Long enough here to read a paragraph and
 * act on it; still bounded, so a panel whose dismissal was missed eventually clears itself.
 */
const CELL_REVEAL_TOUCH_HIDE_DELAY_MS = 20000;
/** A formatter that ran out of room leaves an ellipsis behind. */
const TRUNCATED_TEXT_SUFFIX = /(?:\u2026|\.\.\.)\s*$/;

/**
 * Whether this device needs tooltips triggered by focus rather than hover.
 *
 * AG Grid has no touch path at all: `tooltipTrigger` is only 'hover' | 'focus', and the feature
 * binds `mouseenter`/`mouseleave` for one and `focusin`/`focusout` for the other. Worse, on iOS
 * its `onMouseEnter` opens with a bare `if (_isIOSUserAgent()) return;` — so the hover trigger is
 * not merely unreliable there, it is switched off at the source, and no amount of synthetic tap
 * events will raise a tooltip. Focus is the only route, and it works because a tap focuses the
 * cell (cells stay focusable — `suppressCellFocus` is never set).
 *
 * The iOS branch deliberately mirrors AG Grid's own `_isIOSUserAgent`, down to the iPadOS
 * "MacIntel" + maxTouchPoints test, because that exact predicate is what disables their hover
 * path. Matching it keeps an iPad driven by a trackpad — which reports `(hover: hover)` and would
 * otherwise be handed the hover trigger — from ending up with no tooltips whatsoever. Everything
 * else is asked the honest question rather than sniffed: `(hover: none)` is precisely the
 * condition under which a hover trigger cannot fire.
 */
function usesFocusTooltipTrigger(targetWindow: Window): boolean {
  const nav = targetWindow.navigator;
  const isIOSLike =
    /iPad|iPhone|iPod/.test(nav.platform) ||
    (nav.platform === "MacIntel" && nav.maxTouchPoints > 1);
  if (isIOSLike) {
    return true;
  }
  // Optional-called: jsdom (unit tests) and the odd embedding have no matchMedia.
  return targetWindow.matchMedia?.("(hover: none)").matches ?? false;
}

/**
 * Whether a cell's rendered text is actually clipped by its column, measured on the cell
 * AG Grid laid out. Measuring beats estimating from character counts: the estimate can't
 * know the theme's font, a custom cellRenderer's markup, or the width left after a drag.
 */
function isCellTextClipped(
  gridContainer: HTMLElement,
  colId: string | undefined,
  rowIndex: number | undefined
): boolean {
  if (!colId || rowIndex === undefined || rowIndex < 0) {
    return false;
  }
  // rowIndex is a number, so it's safe to interpolate; col ids come from agent-authored
  // field names, so those are compared as attributes rather than put in a selector.
  // A row can be split across the pinned and centre containers, hence querySelectorAll.
  const rows = Array.from(
    gridContainer.querySelectorAll<HTMLElement>(`.ag-row[row-index="${rowIndex}"]`)
  );
  for (const row of rows) {
    const cell = Array.from(row.querySelectorAll<HTMLElement>(".ag-cell")).find(
      candidate => candidate.getAttribute("col-id") === colId
    );
    if (!cell) {
      continue;
    }
    const textEl = cell.querySelector<HTMLElement>(".ag-cell-value") ?? cell;
    // 1px of slack: sub-pixel layout rounding otherwise reads as a clip.
    return textEl.scrollWidth > textEl.clientWidth + 1;
  }
  return false;
}

/**
 * Whether a cell has earned the reveal panel. Deliberately narrow: numbers and dates are
 * not the "text fields" this is for, and a thousands-separated number is not truncated
 * just because its formatted form differs from the raw one - only a trailing ellipsis
 * (from a formatter that cut the string itself) says that.
 */
export function cellTextNeedsReveal(
  rawValue: unknown,
  displayedText: string,
  isClipped: boolean
): boolean {
  const isTruncatedByFormatter = TRUNCATED_TEXT_SUFFIX.test(displayedText);
  if (typeof rawValue !== "string" && !isTruncatedByFormatter) {
    return false;
  }
  if (!String(rawValue ?? "").trim()) {
    return false;
  }
  return isClipped || isTruncatedByFormatter;
}

/** The slice of AG Grid's tooltip params the reveal gate reads. */
type CellRevealGateParams = {
  value?: unknown;
  column?: { getColId?: () => string };
  rowIndex?: number;
};

/** The text a cell actually shows: the agent's formatValue, falling back to the raw value. */
function formatCellValueForDisplay(
  formatValue: ((value: unknown) => string) | undefined,
  value: unknown
): string {
  if (!formatValue) {
    return String(value ?? "");
  }
  try {
    return String(formatValue(value) ?? "");
  } catch {
    return String(value ?? "");
  }
}

function createDataTableInternal(
  dataTableContainer: HTMLElement,
  tableOptions: DataTableOptions | ModalDataTableOptions,
  internalOptions: {
    sizeHeightToFit?: boolean;
    showSearch?: boolean;
    showActionsInTable?: boolean;
    /** The block a row drill-down is launched from, so the drawer's edit chip names it. */
    drilldownSource?: { cardElement: HTMLElement; blockType: string };
  } = {}
): CleanupFunction {
  const targetWindow = getTargetWindow();
  const targetDocument = targetWindow.document;
  const { agGrid } = targetWindow;
  if (!agGrid) {
    throw new Error("AG Grid not loaded");
  }

  const sizeHeightToFit = internalOptions.sizeHeightToFit ?? false;
  const showSearch = internalOptions.showSearch ?? true;
  const showActionsInTable = internalOptions.showActionsInTable ?? sizeHeightToFit;
  const drilldownSource = internalOptions.drilldownSource ?? null;
  // The drawer a row click opened; closed before the next one and with the grid.
  let closeRowDrilldown: CleanupFunction | null = null;
  const DATA_TABLE_ACTIONS_OFFSET = "var(--data-table-actions-offset, 80px)";

  // Mobile drill-down modals swap infinite scroll for a "Load more" footer: AG Grid pagination
  // stays enabled (paging bar hidden via the marker class) pinned to page 0, and the footer grows
  // the page size so rows accumulate (all rows are already loaded client-side, so this is
  // instant). Gate on showActionsInTable (unique to the drill-down modal path) + a mobile
  // viewport. Dashboard block tables keep infinite scroll everywhere.
  const loadMoreMode =
    showActionsInTable && targetWindow.innerWidth < AG_GRID_SIZE_TO_FIT_MIN_WIDTH;

  // Every table renders its cell tooltips as the reveal panel — the card carrying the full value,
  // a Copy button and a close affordance — rather than AG Grid's plain text bubble. A tooltip
  // that exists to recover text the column clipped is only half a feature if the recovered text
  // can't be taken anywhere; the panel is also what caps a 2,000-character value at a scrollable
  // height instead of painting one enormous bubble across the rows.
  //
  // One component instance per grid, assigned per column below: `tooltipComponent` is a COLUMN
  // property in AG Grid 34, not a grid option (setting it on gridOptions is silently ignored,
  // with an "invalid gridOptions property" warning).
  const cellRevealTooltipComponent = createCellRevealTooltipComponent(targetWindow, targetDocument);

  // Drill-down modals go further and decide WHETHER a tooltip appears in JS, rather than leaning
  // on the grid's `tooltipShowMode` (see the tooltipValueGetter assignments below). Their cells
  // stay on one ellipsized line inside a drawer whose height snaps to whole rows, so widening a
  // column isn't the escape it is in a resizable block — and a JS measurement also catches the
  // case a CSS-overflow check cannot: a cell that FITS because its own formatValue truncated it.
  // Gate on showActionsInTable, the flag unique to the drill-down path.
  const revealClippedCellText = showActionsInTable;
  /** Measures the hovered cell in THIS grid; gridContainer is created further down. */
  const isCellRevealClipped = (params: CellRevealGateParams): boolean =>
    isCellTextClipped(gridContainer, params.column?.getColId?.(), params.rowIndex);
  const loadMoreStep = tableOptions.pageSize ?? 20;
  let visibleCount = loadMoreStep;

  // Create search input if enabled
  let searchInput: HTMLInputElement | null = null;
  let searchInputHandler: ((e: Event) => void) | null = null;
  let searchKeydownHandler: ((e: KeyboardEvent) => void) | null = null;
  let searchFocusHandler: (() => void) | null = null;
  let searchBlurHandler: (() => void) | null = null;
  let clearButton: HTMLSpanElement | null = null;
  let debouncedFilterUpdate: (((value: string) => void) & { cancel: () => void }) | null = null;

  // Clear any existing content (prevents duplication on theme change)
  dataTableContainer.innerHTML = "";

  // Make the container relatively positioned for absolute positioning of search box
  // Also make it a flex column so grid stacks properly
  dataTableContainer.classList.add("relative", "flex", "flex-col");

  // Stable key for session-scoped column-width memory (null for modal drill-downs,
  // which aren't hosted in a dashboard card and shouldn't share/persist widths).
  // Prefixed with the dashboard id so same-named blocks on different dashboards
  // don't share state (see _dataTableMemoryNamespace).
  const hostCard = dataTableContainer.closest<HTMLElement>(".themed-dashboard-card");
  const resizeMemoryKey = hostCard?.id ? `${_dataTableMemoryNamespace ?? ""}:${hostCard.id}` : null;

  // Manual adjustments layer: persisted `table_overrides` from the block config
  // (survives reloads) under this session's drags (latest wins). The block id is
  // stamped on the card by DataTableBlock; modal drill-downs have no host card
  // and get neither layer.
  const overridesBlockId = hostCard?.dataset.coeffBlockId || null;
  const persistedOverrides = overridesBlockId
    ? _tableOverridesByBlockId[overridesBlockId]
    : undefined;
  const rememberedWidths: Record<string, number> = {
    ...persistedOverrides?.column_widths,
    ...(resizeMemoryKey ? _columnWidthMemory.get(resizeMemoryKey) : undefined),
  };
  const rememberedOrder =
    (resizeMemoryKey ? _columnOrderMemory.get(resizeMemoryKey) : undefined) ??
    persistedOverrides?.column_order;
  // Whole-map preference (session ?? persisted), not a per-key merge: the session
  // map is complete once the user edits, and a key deleted there means "cleared".
  const rememberedHeaders: Record<string, string> =
    (resizeMemoryKey ? _columnHeaderMemory.get(resizeMemoryKey) : undefined) ??
    persistedOverrides?.column_headers ??
    {};

  const buildCsvText = (lineEnding: string, includeTrailingNewline: boolean): string => {
    const { headers, rows } = exportMatrix();
    const csv = [
      headers.map(h => escapeForCsv(formatCellForExport(h))).join(","),
      ...rows.map(r => r.map(v => escapeForCsv(v)).join(",")),
    ].join(lineEnding);
    return includeTrailingNewline ? `${csv}${lineEnding}` : csv;
  };

  const getExportTitle = (): string => {
    if ("title" in tableOptions && typeof tableOptions.title === "string") {
      return sanitizeFileNamePart(tableOptions.title);
    }
    const card = dataTableContainer.closest(".themed-dashboard-card");
    const h2 = card?.querySelector("h2");
    const text = h2?.textContent ?? "";
    return sanitizeFileNamePart(text);
  };

  const getFilteredSortedRows = (): Array<Record<string, unknown>> => {
    const rows: Array<Record<string, unknown>> = [];
    const api = gridApiRef.current;
    if (!api) {
      return rows;
    }
    const forEach =
      api.forEachNodeAfterFilterAndSort || api.forEachNodeAfterFilter || api.forEachNode;
    if (typeof forEach !== "function") {
      return rows;
    }
    forEach.call(api, (node: { data?: Record<string, unknown>; group?: boolean }) => {
      if (node?.group) {
        return;
      }
      if (node?.data) {
        rows.push(node.data);
      }
    });

    if (typeof api.getPinnedBottomRowCount === "function") {
      const count = api.getPinnedBottomRowCount();
      for (let i = 0; i < count; i++) {
        const pinnedNode = api.getPinnedBottomRow(i);
        if (pinnedNode?.data) {
          rows.push(pinnedNode.data);
        }
      }
    }

    return rows;
  };

  const exportMatrix = (): { headers: string[]; rows: string[][] } => {
    // Export in the order the user sees: live grid order first (covers columns
    // dragged since creation), then the creation-time display order (which
    // already includes persisted/session reorders). Cells are looked up by
    // `field`, so any column order is safe.
    const liveOrderedDefs: typeof columnDefs = [];
    for (const gridColumn of gridApiRef.current?.getAllGridColumns?.() ?? []) {
      const field = gridColumn.getColDef?.()?.field;
      const def = field ? columnDefs.find(d => d.field === field) : undefined;
      if (def) {
        liveOrderedDefs.push(def);
      }
    }
    const exportDefs =
      liveOrderedDefs.length === columnDefs.length ? liveOrderedDefs : displayColumnDefs;
    const headers = exportDefs.map(col => col.headerName ?? col.field);
    const rows = getFilteredSortedRows().map(rowObj => {
      return exportDefs.map(col => {
        const rawValue = rowObj[col.field];
        const formatted =
          typeof col.valueFormatter === "function"
            ? col.valueFormatter({ value: rawValue })
            : rawValue;
        return formatCellForExport(formatted);
      });
    });
    return { headers, rows };
  };

  // The grid API is created after column defs/row data are set up.
  // Store it so action buttons can export filtered/sorted data.
  const gridApiRef = { current: null as any };

  if (showSearch) {
    // Create search wrapper absolutely positioned relative to data-table-container
    const searchWrapper = targetDocument.createElement("div");
    searchWrapper.className = "themed-data-table-search-wrapper";
    // Position at top-right, offset upwards above the table
    searchWrapper.style.position = "absolute";
    searchWrapper.style.top = "-44px"; // Offset upwards (should align vertically with table title)
    searchWrapper.style.right = DATA_TABLE_ACTIONS_OFFSET; // Offset rightwards (avoids overlapping with actions buttons)
    searchWrapper.style.zIndex = "10";

    const searchIcon = targetDocument.createElement("span");
    searchIcon.className = "material-icons themed-data-table-search-icon";
    searchIcon.textContent = "search";

    searchInput = targetDocument.createElement("input");
    searchInput.type = "text";
    searchInput.placeholder = "Search";
    searchInput.className = "themed-data-table-search";
    searchInput.name = "data-table-search-input";
    // Re-seed from the session memory the grid also seeds its quickFilterText
    // from, so a re-render shows the still-active filter in the box instead of
    // filtered rows under an empty search bar.
    const rememberedFilter = resizeMemoryKey ? _quickFilterMemory.get(resizeMemoryKey) : undefined;
    if (rememberedFilter) {
      searchInput.value = rememberedFilter;
    }

    // Create clear button (hidden while the input is empty)
    clearButton = targetDocument.createElement("span");
    clearButton.className = "material-icons themed-data-table-search-clear";
    clearButton.textContent = "close";
    clearButton.style.display = searchInput.value ? "block" : "none";

    searchWrapper.appendChild(searchIcon);
    searchWrapper.appendChild(searchInput);
    searchWrapper.appendChild(clearButton);
    // Append to dataTableContainer as absolutely positioned child
    dataTableContainer.appendChild(searchWrapper);
  }

  // Create a separate container for the AG Grid. Its height is set explicitly by
  // sizeDataTableGrid (content height, clamped to the block) rather than flexed to
  // fill, so a short table hugs its rows (auto-collapse) and a tall one scrolls.
  const gridContainer = targetDocument.createElement("div");
  gridContainer.className = "ag-theme-material coeff-data-table-grid";
  if (loadMoreMode) {
    // Marker class: WebDashboardContent's ExtraStyles hides AG Grid's paging bar under it.
    gridContainer.classList.add("coeff-data-table-load-more");
  }
  dataTableContainer.appendChild(gridContainer);

  // Mobile drill-down "Load more" footer: a visible-row-range summary + a button that reveals the
  // next slice of already-loaded rows (accumulating) and auto-scrolls the grid to them. It sits
  // below the height-capped grid, so AG Grid's column header stays pinned and this footer stays
  // visible.
  let loadMoreFooter: HTMLDivElement | null = null;
  let loadMoreRangeEl: HTMLSpanElement | null = null;
  let loadMoreButton: HTMLButtonElement | null = null;
  let loadMoreClickHandler: (() => void) | null = null;

  const updateLoadMoreFooter = (gridApi: DataTableSizingGridApi): void => {
    if (!loadMoreRangeEl || !loadMoreButton) {
      return;
    }
    const total = gridApi.paginationGetRowCount?.() ?? 0;
    const shown = Math.min(visibleCount, total);
    loadMoreRangeEl.textContent = total === 0 ? "0 of 0" : `1 – ${shown} of ${total}`;
    loadMoreButton.style.display = shown < total ? "" : "none";
  };

  if (loadMoreMode) {
    // Layout per Figma 5750-5372: the row-range summary and "Load more" sit together, centered in
    // the row (not pushed to opposite edges), both 14px medium — the range muted, "Load more" in
    // the primary foreground. text-[14px] keeps it exactly 14px (rem utilities scale in the viewer
    // iframe).
    loadMoreFooter = targetDocument.createElement("div");
    loadMoreFooter.className =
      "sticky bottom-0 z-10 flex items-center justify-center gap-[10px] border-t border-main-border bg-main-background py-[7px]";

    loadMoreRangeEl = targetDocument.createElement("span");
    loadMoreRangeEl.className = "text-[14px] font-medium text-main-muted-foreground";

    loadMoreButton = targetDocument.createElement("button");
    loadMoreButton.type = "button";
    loadMoreButton.className =
      "text-[14px] font-medium text-main-foreground hover:opacity-70 cursor-pointer";
    loadMoreButton.textContent = "Load more";
    loadMoreButton.style.display = "none";
    loadMoreClickHandler = () => {
      const api = gridApiRef.current as AgGridGlobals.GridApi | null;
      if (!api) {
        return;
      }
      // Grow the page-0 slice; onPaginationChanged re-caps the grid height and updates the footer.
      visibleCount += loadMoreStep;
      api.setGridOption("paginationPageSize", visibleCount);
      // Reveal the newly loaded rows: scroll AG Grid's body so the last visible row is at the
      // bottom, once the grid has re-laid out. ensureIndexVisible drives AG's internal scroll
      // correctly.
      targetWindow.requestAnimationFrame(() => {
        const total = api.paginationGetRowCount?.() ?? 0;
        const lastIndex = Math.min(visibleCount, total) - 1;
        if (lastIndex >= 0) {
          api.ensureIndexVisible(lastIndex, "bottom");
        }
      });
    };
    loadMoreButton.addEventListener("click", loadMoreClickHandler);

    loadMoreFooter.appendChild(loadMoreRangeEl);
    loadMoreFooter.appendChild(loadMoreButton);
    dataTableContainer.appendChild(loadMoreFooter);
  }

  const columnDefs = tableOptions.columns.map((column, columnIndex) => {
    if (typeof column === "string") {
      return {
        field: column,
        headerTooltip: column,
        tooltipComponent: cellRevealTooltipComponent,
        // A column can be declared either as a bare string or as an object, and this shorthand
        // branch returns early — so it never reaches the tooltip wiring further down, and would
        // otherwise be the one column shape left with no hover tooltip at all.
        //
        // Same rule as the object form: reveal the value only when the cell's text is actually
        // clipped, returning "" otherwise so no panel opens over a cell the reader can already
        // read in full. A string column has no formatValue and no cellRenderer, so the raw value
        // already IS what is on screen — there is no formatted value worth preferring over it.
        tooltipValueGetter: (params: CellRevealGateParams) =>
          isCellRevealClipped(params) ? String(params.value ?? "") : "",
      };
    }

    const isNumericOrDateColumn = tableOptions.data
      .slice(0, 30)
      .some(row => isNumericOrDateValue(row[columnIndex]));

    // Sort control: an explicit comparator always wins; otherwise a column whose
    // cells are date-like label strings (e.g. "Jul 21, 2025") sorts chronologically
    // rather than alphabetically. Numeric/Date/text columns are left on AG Grid's
    // default comparator.
    const comparator =
      column.comparator ??
      (isDateLikeColumn(tableOptions.data, columnIndex) ? dateLabelComparator : undefined);

    const valueFormatter = ({ value }: { value: unknown }) => {
      if (!column.formatValue) {
        return value;
      }
      try {
        return column.formatValue(value);
      } catch (error) {
        return value;
      }
    };

    const internalCellClasses: string[] = [];
    if (column.highlight) {
      internalCellClasses.push("bg-coral");
    }
    if (isNumericOrDateColumn) {
      internalCellClasses.push("ag-right-aligned-cell");
    }

    const baseCellClass = mergeColumnCellClass(internalCellClasses, column.cellClass);

    // Total (pinned bottom) rows get a deliberately quiet treatment: keep the
    // column's alignment but strip conditional formatting (the internal `bg-coral`
    // highlight plus any `bg-*` classes the agent applied) so colours meant for
    // data rows don't bleed into the summary row.
    const cellClass = (params: CellRendererParams) => {
      const resolved = typeof baseCellClass === "function" ? baseCellClass(params) : baseCellClass;
      const classes = Array.isArray(resolved) ? resolved : resolved ? [resolved] : [];
      if (isPinnedBottomRow(params)) {
        return classes.filter(c => !c.startsWith("bg-"));
      }
      return classes;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const colDef: Record<string, any> = {
      field: column.field,
      headerName: column.headerName,
      // Reveal the full column name on hover — headers truncate at narrow widths.
      headerTooltip: column.headerName ?? column.field,
      tooltipComponent: cellRevealTooltipComponent,
      valueFormatter,
      getQuickFilterText: (params: { value: unknown }) =>
        formatCellValueForDisplay(column.formatValue, params.value),
      cellClass,
      headerClass: isNumericOrDateColumn ? "coeff-ag-right-aligned-header" : undefined,
    };

    if (comparator) {
      colDef.comparator = comparator;
    }

    if (column.cellRenderer) {
      // Wrapped so a nullish return falls back to formatValue instead of blanking
      // the cell — see wrapCellRendererForAgGrid.
      colDef.cellRenderer = wrapCellRendererForAgGrid(column.cellRenderer, column.formatValue);
    } else if (column.link !== undefined) {
      // Link affordance: the value underlined, plus an external-link icon that fades in on
      // hover. The explicit `cellRenderer` above takes precedence when both are set.
      const linkOption = column.link;
      const hrefFor = typeof linkOption === "function" ? linkOption : () => linkOption;
      const textFormatter = (value: unknown): string =>
        column.formatValue ? column.formatValue(value) : String(value ?? "");
      colDef.cellRenderer = buildLinkCellRenderer(hrefFor, textFormatter);
    }
    if (column.cellStyle) {
      const userCellStyle = column.cellStyle;
      // Mirror the cellClass rule for inline styles: drop background/text colours
      // from the total row so conditional formatting doesn't carry into it. A
      // throwing agent-supplied style function degrades to "no styling" for that
      // cell (same defensive posture as valueFormatter above).
      colDef.cellStyle = (params: CellRendererParams) => {
        let resolved: Record<string, string> | undefined;
        try {
          resolved = typeof userCellStyle === "function" ? userCellStyle(params) : userCellStyle;
        } catch {
          return undefined;
        }
        const style = withReadableTextColor(resolved);
        if (style && isPinnedBottomRow(params)) {
          const { background, backgroundColor, color, ...rest } = style;
          return rest;
        }
        return style;
      };
    }
    if (column.width !== undefined) {
      colDef.width = column.width;
      // Drop the per-column minWidth to the requested width so the global
      // defaultColDef.minWidth (DATA_TABLE_MIN_RESIZE_WIDTH = 48) doesn't
      // silently clamp a very narrow explicit width (e.g. width:30 -> 48).
      colDef.minWidth = column.width;
    }
    if (column.flex !== undefined) {
      colDef.flex = column.flex;
    }
    if (column.pinned) {
      colDef.pinned = column.pinned;
    }
    if (column.tooltip === undefined && revealClippedCellText) {
      // No agent-supplied tooltip: default to revealing the raw value whenever the
      // cell's own text is clipped. Returning "" tells AG Grid there is no tooltip,
      // so cells that fit stay quiet.
      colDef.tooltipValueGetter = (params: CellRevealGateParams) => {
        const displayed = formatCellValueForDisplay(column.formatValue, params.value);
        return cellTextNeedsReveal(params.value, displayed, isCellRevealClipped(params))
          ? String(params.value ?? "")
          : "";
      };
      // That gate is meant to be the only one these columns answer to, so take them out of the
      // grid's `tooltipShowMode: "whenTruncated"` measurement the same way an explicit `tooltip`
      // does — by making them renderer columns (see buildDefaultCellRenderer). Left in, the two
      // gates would AND together and the stricter one would quietly win: a value its own
      // formatValue truncated still fits its column, so the CSS-overflow check would suppress the
      // very panel cellTextNeedsReveal just asked for. Columns that already carry a renderer (an
      // explicit `cellRenderer`, or `link` — both assigned above, which is why that assignment
      // has to stay ahead of this one) are exempt already and keep their own rendering.
      if (colDef.cellRenderer === undefined) {
        colDef.cellRenderer = buildDefaultCellRenderer(column.formatValue);
      }
    }
    if (column.tooltip !== undefined) {
      // Normalise the three accepted tooltip shapes into AG Grid's
      // tooltipValueGetter contract: (params) => string.
      const tip = column.tooltip;
      if (typeof tip === "function") {
        const tipFn = tip;
        colDef.tooltipValueGetter = (params: CellRendererParams) => {
          try {
            return String(tipFn(params) ?? "");
          } catch {
            return "";
          }
        };
      } else if (typeof tip === "string") {
        colDef.tooltipValueGetter = () => tip;
      } else if (tip === true) {
        // tooltip:true reveals the RAW (un-formatted) cell value — the common
        // "show full text on hover when the cell is truncated" request. The
        // formatted value would just duplicate what's already visible.
        colDef.tooltipValueGetter = (params: { value: unknown }) => String(params.value ?? "");
      }

      // The branches above turned `column.tooltip` into a getter; what is left is deciding when
      // that tooltip is allowed to appear. An explicit tooltip has to show on every hover rather
      // than only on a clipped cell: a static note has nothing to do with truncation, and
      // `tooltip: true` is actively inverted by a truncation gate — `formatValue` shortens the
      // text, so the cell stops overflowing, so the gate suppresses the very tooltip that was
      // asked for in order to reveal the full value.
      //
      // Rather than flip the whole grid, opt this one column out by making it a renderer column,
      // which AG Grid excludes from truncation measurement (see buildDefaultCellRenderer). The
      // grid stays on "whenTruncated" for everyone else.
      //
      // Both conditions are load-bearing. Skip columns that already have a renderer — an explicit
      // `cellRenderer`, or `link` — since they are exempt already and overwriting one would
      // replace the agent's own rendering with plain text; that is why the `cellRenderer`/`link`
      // assignment above has to stay ahead of this block. And gate on the getter rather than on
      // `column.tooltip`, because `tooltip: false` is an opt-OUT that matches none of the three
      // branches above and must not end up always-showing.
      if (colDef.tooltipValueGetter !== undefined && colDef.cellRenderer === undefined) {
        colDef.cellRenderer = buildDefaultCellRenderer(column.formatValue);
      }
    }

    // Columns here are sized to fit the grid width rather than their content, so clipped text is
    // the normal case in these tables — and until now there was no way to read what got cut off.
    // This gives every column that has not opted out a hover tooltip carrying its full value.
    //
    // It opens ONLY when the cell's text is genuinely cut off, measured on the cell AG Grid
    // actually laid out (isCellTextClipped). One rule, every column type — which takes a bit of
    // explaining, because AG Grid will not give it to us on its own.
    //
    // The grid's `tooltipShowMode: "whenTruncated"` covers plain-text columns, but it flatly
    // refuses to measure a renderer column: its tooltip wiring gates on `!ctrl.isCellRenderer()`
    // rather than inspecting arbitrary renderer DOM. Pills, badges, emoji text, hand-rolled
    // links and the `link` option therefore fall through to AG Grid's unconditional branch and
    // would pop the panel on EVERY hover — including over cells that are fully readable where
    // they sit. Measuring here closes that gap, so a pill that fits stays as quiet as a plain
    // cell that fits.
    //
    // An explicit `tooltip` is the one thing still exempt, and deliberately so: an author asking
    // for a tooltip is making a statement about the content, not about the column's width.
    //
    // The getter prefers `valueFormatted` because that is the text the reader actually sees,
    // including for the `link` renderer, whose DOM ends in the Material-icon ligature
    // "open_in_new". Note this is the mirror image of `tooltip: true` above, which deliberately
    // shows the RAW value in order to undo a formatValue that truncates.
    //
    // Two quiet edge cases fall out of this. A blank cell shows no bubble at all, because an
    // empty string fails AG Grid's `_exists` check in showTooltip(). And `tooltip: false` opts
    // out here, which works precisely because `false` matches none of the three branches above
    // that would have set a getter.
    if (colDef.tooltipValueGetter === undefined && column.tooltip !== false) {
      colDef.tooltipValueGetter = (params: CellRevealGateParams & { valueFormatted?: unknown }) => {
        if (!isCellRevealClipped(params)) {
          return "";
        }
        return params.valueFormatted != null
          ? String(params.valueFormatted)
          : String(params.value ?? "");
      };
    }

    // Per-column sizing (all optional — columns without these are unchanged).
    // An explicit `width` is pinned so the `fitGridWidth` auto-size pass can't
    // stretch/shrink it: we set suppressSizeToFit AND lock min/max to the width, so it
    // holds regardless of how the declarative strategy treats suppressSizeToFit. The
    // other (unpinned) columns still size-to-fit the remaining grid width. Explicit
    // minWidth/maxWidth below override the lock when the caller sets them.
    if (typeof column.width === "number") {
      colDef.width = column.width;
      colDef.suppressSizeToFit = true;
      colDef.minWidth = column.width;
      colDef.maxWidth = column.width;
    }
    if (typeof column.minWidth === "number") {
      colDef.minWidth = column.minWidth;
    }
    if (typeof column.maxWidth === "number") {
      colDef.maxWidth = column.maxWidth;
    }
    if (typeof column.flex === "number") {
      colDef.flex = column.flex;
    }

    // Reapply a width the user dragged — persisted table_overrides layered under
    // this session's drags. Only when the caller didn't hard-code a width;
    // excluded from the fit pass so it holds, but left resizable (no min/max
    // lock) so the user can adjust again.
    const rememberedWidth = rememberedWidths[column.field];
    if (rememberedWidth !== undefined && column.width === undefined) {
      colDef.width = rememberedWidth;
      colDef.suppressSizeToFit = true;
    }

    return colDef;
  });

  const rowData = tableOptions.data.map(row =>
    columnDefs.reduce((acc, columnDef, index) => {
      acc[columnDef.field] = row[index];
      return acc;
    }, {} as Record<string, unknown>)
  );

  // Initial height before the first measured sizing pass in onGridReady.
  gridContainer.style.height = "300px";

  const toPinnedRows = (rows: unknown[][] | undefined) =>
    rows?.filter(Array.isArray).map(row => {
      // Blank the agent's dash placeholders so empty summary cells read as empty.
      const cells = row.map(value => (isDashPlaceholder(value) ? "" : value));
      // Ensure a "Total" label lives in the leftmost cell when it would otherwise be
      // blank — the total row should always be identifiable without a filled bar.
      if (cells[0] == null || cells[0] === "") {
        cells[0] = "Total";
      }
      return columnDefs.reduce((acc, columnDef, index) => {
        acc[columnDef.field] = cells[index];
        return acc;
      }, {} as Record<string, unknown>);
    });

  const computePinnedRows = buildPinnedRowsComputer(tableOptions);

  const pinnedBottomRows = toPinnedRows(
    computePinnedRows ? computePinnedRows(tableOptions.data) : tableOptions.pinnedBottomRowData
  );

  // Live totals: rebuild the pinned rows from the post-filter node set. Hooked on
  // filterChanged (not modelUpdated) because setting pinnedBottomRowData itself
  // re-fires modelUpdated. Export reads pinned rows back off the grid, so the CSV
  // total stays consistent with its filtered rows.
  const recomputePinnedRows = (gridApi: AgGridGlobals.GridApi<Record<string, unknown>>) => {
    if (!computePinnedRows || gridApi.isDestroyed?.()) {
      return;
    }
    if (
      typeof gridApi.forEachNodeAfterFilter !== "function" ||
      typeof gridApi.setGridOption !== "function"
    ) {
      return;
    }
    const filteredRows: unknown[][] = [];
    gridApi.forEachNodeAfterFilter(node => {
      if (node.data) {
        filteredRows.push(columnDefs.map(columnDef => node.data?.[columnDef.field]));
      }
    });
    // Agent-authored callback: a throw here would otherwise escape AG Grid's event
    // dispatch into the search-box handlers. Keep the last good pinned rows instead.
    try {
      gridApi.setGridOption("pinnedBottomRowData", toPinnedRows(computePinnedRows(filteredRows)));
    } catch (error) {
      console.warn("computePinnedBottomRowData failed; keeping previous totals", error);
    }
  };

  // Reapply user-typed header renames. Originals are captured first so clearing
  // a rename restores the JS headerName — or, when the JS never set one, lets
  // AG Grid re-derive its default from the field again (headerName deleted).
  const headerDefs = columnDefs as Array<{
    field?: string;
    headerName?: string;
    headerTooltip?: string;
  }>;
  const originalHeaderByField = new Map<string, string | undefined>(
    headerDefs.filter(def => def.field).map(def => [def.field as string, def.headerName])
  );
  for (const def of headerDefs) {
    const alias = def.field ? rememberedHeaders[def.field] : undefined;
    if (alias) {
      def.headerName = alias;
      def.headerTooltip = alias;
    }
  }

  // Reapply a user-dragged column order (persisted table_overrides layered under
  // this session's drags). Must happen AFTER rowData/pinnedBottomRows are built —
  // those map data cells to fields by the ORIGINAL column index.
  const displayColumnDefs = applyColumnOrder(columnDefs, rememberedOrder);

  // Always fit columns to the grid width. Columns with an explicit `width` are
  // already pinned (suppressSizeToFit + locked min/max above), so fitGridWidth /
  // sizeColumnsToFit leave them untouched and only distribute the remaining width
  // across the other columns — exactly the intended behaviour. The old
  // "fitProvidedWidth" branch was invalid: that strategy REQUIRES a `width`, and
  // omitting it made AG Grid fit columns to an undefined width, collapsing them all
  // to their min-widths bunched at the left of the card (Jul 2026 regression).
  const autoSizeStrategy = {
    type: "fitGridWidth",
    defaultMinWidth: DATA_TABLE_MIN_COLUMN_WIDTH,
  } as AgGridGlobals.GridOptions["autoSizeStrategy"];

  const pinnedBottomRowCount = pinnedBottomRows?.length ?? 0;
  const totalRowCount = rowData.length;

  const formatRowCountLabel = (displayedCount: number): string => {
    const noun = totalRowCount === 1 ? "row" : "rows";
    return displayedCount >= totalRowCount
      ? `${totalRowCount.toLocaleString()} ${noun}`
      : `${displayedCount.toLocaleString()} of ${totalRowCount.toLocaleString()} ${noun}`;
  };
  // End-of-table footer, drill-down modals only: infinite scroll has no pager, so
  // this states the row count to signal the view is complete. It lives below the
  // grid in the flex container and is measured into the grid's usable height. The
  // mobile drill-down "Load more" footer replaces it (shows its own range).
  // Standalone blocks render NO footer — a text line under the grid read as extra
  // card whitespace; the count sits inline with the card title instead (below).
  const footer = targetDocument.createElement("div");
  footer.className = "coeff-data-table-footer";
  if (sizeHeightToFit && !loadMoreMode) {
    dataTableContainer.appendChild(footer);
  }
  // Standalone blocks: the count sits ON the title line, absolutely anchored to
  // the title row's right edge — no extra card height. It fades out on block
  // hover, when the action-icon overlay claims that corner (see the
  // .coeff-data-table-row-count rules in WebDashboardContent). Reuse an element
  // a previous grid instance left behind (custom render JS can call
  // createDataTable more than once).
  let blockRowCount: HTMLElement | null = null;
  if (!sizeHeightToFit) {
    const titleRow = dataTableContainer
      .closest<HTMLElement>(".themed-dashboard-card")
      ?.querySelector<HTMLElement>(".block-title-row");
    if (titleRow) {
      blockRowCount = titleRow.querySelector<HTMLElement>("[data-coeff-block-row-count]");
      if (!blockRowCount) {
        blockRowCount = targetDocument.createElement("div");
        blockRowCount.setAttribute("data-coeff-block-row-count", "");
        blockRowCount.className = "coeff-data-table-row-count";
        titleRow.appendChild(blockRowCount);
      }
    }
  }
  // custom_html-hosted tables own their card markup and render no
  // .block-title-row, so there's no title line to carry the count — fall back
  // to the classic under-grid footer there rather than silently dropping it.
  // Fill-mode sizing already subtracts the measured footer height.
  if (!sizeHeightToFit && !loadMoreMode && !blockRowCount) {
    dataTableContainer.appendChild(footer);
  }
  const updateRowCountDisplays = (displayedCount: number): void => {
    const label = formatRowCountLabel(displayedCount);
    footer.textContent = label;
    if (blockRowCount) {
      blockRowCount.textContent = label;
    }
  };
  updateRowCountDisplays(totalRowCount);

  // Modal drill-downs aren't flex-sized, so grow-to-content up to a viewport cap
  // (further capped to the drawer inside sizeDataTableGrid) and scroll beyond it;
  // dashboard blocks clamp to the space the block gives us.
  const modalMaxHeight = Math.round((targetWindow.innerHeight || 800) * 0.7);
  // Frozen-column divider visibility: the pinned column's vertical border only
  // appears once content is actually sliding underneath it (scrollLeft > 0) —
  // see the .coeff-hscrolled rules in WebDashboardContent. Synced from AG
  // Grid's body-scroll events and from applySizing (covers programmatic scroll
  // resets after column/container resizes that don't fire a scroll event).
  const syncPinnedDividerState = (scrollLeft?: number): void => {
    const left =
      scrollLeft ??
      gridContainer.querySelector<HTMLElement>(".ag-center-cols-viewport")?.scrollLeft ??
      0;
    gridContainer.classList.toggle("coeff-hscrolled", left > 0);
  };

  let lastReported: TableContentHeightReport = { natural: 0, hugged: 0 };
  const applySizing = (
    gridApi: DataTableSizingGridApi & {
      getDisplayedRowCount?: () => number;
      getPinnedBottomRowCount?: () => number;
    }
  ) => {
    // Debounced resize/timeout callbacks can fire after a slicer or theme change
    // destroyed this grid instance; AG Grid throws on API calls after destroy.
    if (gridApi.isDestroyed?.()) {
      return;
    }
    syncPinnedDividerState();
    const displayed = gridApi.getDisplayedRowCount?.() ?? totalRowCount;
    updateRowCountDisplays(displayed);
    const footerHeight = footer.getBoundingClientRect().height;
    const { gridHeight, contentHeight } = sizeDataTableGrid(
      gridContainer,
      dataTableContainer,
      gridApi,
      gridApi.getPinnedBottomRowCount?.() ?? pinnedBottomRowCount,
      sizeHeightToFit
        ? {
            // Reserve everything rendered below the grid inside the drawer. Only one
            // footer ever exists (the desktop row-count footer, or the mobile "Load
            // more" one), so summing is safe. Unreserved, the grid is sized a footer
            // taller than the drawer can show, which pushes the footer past the fold
            // and throws the whole-row snap off by that much - the sliced/overlapping
            // bottom row of ENG-7527.
            mode: "cap",
            maxHeight: modalMaxHeight,
            bottomReservePx: (loadMoreFooter?.offsetHeight ?? 0) + footerHeight,
          }
        : { mode: "fill", footerHeight }
    );
    if (loadMoreMode) {
      updateLoadMoreFooter(gridApi);
    }
    // Report the block's card heights (chrome above the table + grid + footer
    // + content below the table + card bottom padding) so the layout can size a
    // table-only row to its content: `natural` uses the unclamped grid content
    // (collapse under short content, grow-to-fit near misses), `hugged` uses
    // the grid as actually rendered — clamped and snapped to a whole row — so
    // an overflowing table's snap remainder doesn't sit as a dead band above
    // the card's bottom padding. chromeBelow counts content custom render JS
    // appends after the table container (e.g. a legend note) — unmeasured, it
    // under-reports the card height and the row squeezes the grid by that
    // amount every pass, an endless report → resize flicker loop. Fill mode
    // only — modal drill-downs have no layout row. The 2px guard stops
    // report → row resize → onGridSizeChanged → report loops on sub-pixel
    // measurement jitter.
    if (!sizeHeightToFit && overridesBlockId && hostCard && _onTableContentHeight) {
      const blockRoot = hostCard.closest<HTMLElement>("[data-block-id]") ?? hostCard;
      const blockRootRect = blockRoot.getBoundingClientRect();
      const containerRect = dataTableContainer.getBoundingClientRect();
      const cardPaddingBottom = parseFloat(getComputedStyle(hostCard).paddingBottom) || 0;
      const { natural, hugged } = computeTableContentHeightReport(
        {
          chromeAbove: containerRect.top - blockRootRect.top,
          chromeBelow: Math.max(0, blockRootRect.bottom - containerRect.bottom - cardPaddingBottom),
          footerHeight,
          cardPaddingBottom,
        },
        { gridHeight, contentHeight }
      );
      if (
        Number.isFinite(natural) &&
        natural > 0 &&
        Number.isFinite(hugged) &&
        hugged > 0 &&
        (Math.abs(natural - lastReported.natural) > 2 || Math.abs(hugged - lastReported.hugged) > 2)
      ) {
        lastReported = { natural, hugged };
        _onTableContentHeight(overridesBlockId, lastReported);
      }
    }
  };

  /** Current display order of column `field`s, read live from the grid. */
  const readLiveColumnOrder = (): string[] => {
    const fields: string[] = [];
    for (const gridColumn of gridApiRef.current?.getAllGridColumns?.() ?? []) {
      const field = gridColumn.getColDef?.()?.field;
      if (field) {
        fields.push(field);
      }
    }
    return fields;
  };

  // Hand the block's complete manual-adjustment state to the persistence hook
  // (no-op for viewers — the hook is only set for editors). Widths merge the
  // persisted layer under this session's drags; order and headers prefer this
  // session's whole-map state, then the persisted one. The persisted layer is
  // read LIVE from module state, not from the creation-time capture — an AI
  // edit (or another save) can change it while this grid instance is alive, and
  // merging over the stale capture would write outdated values back.
  const notifyTableOverridesChanged = (): void => {
    if (!overridesBlockId || !_onTableOverridesChange) {
      return;
    }
    const livePersisted = _tableOverridesByBlockId[overridesBlockId];
    const widths = {
      ...livePersisted?.column_widths,
      ...(resizeMemoryKey ? _columnWidthMemory.get(resizeMemoryKey) : undefined),
    };
    const order =
      (resizeMemoryKey ? _columnOrderMemory.get(resizeMemoryKey) : undefined) ??
      livePersisted?.column_order;
    const headers =
      (resizeMemoryKey ? _columnHeaderMemory.get(resizeMemoryKey) : undefined) ??
      livePersisted?.column_headers;
    const overrides: DataTableUserOverrides = {};
    if (Object.keys(widths).length > 0) {
      overrides.column_widths = widths;
    }
    if (order && order.length > 0) {
      overrides.column_order = order;
    }
    if (headers && Object.keys(headers).length > 0) {
      overrides.column_headers = headers;
    }
    _onTableOverridesChange(overridesBlockId, overrides);
  };

  const seededQuickFilter = resizeMemoryKey ? _quickFilterMemory.get(resizeMemoryKey) ?? "" : "";

  const agGridOptions = {
    columnDefs: displayColumnDefs,
    rowData,
    pinnedBottomRowData: pinnedBottomRows,
    defaultColDef: { filter: true, sortable: true, minWidth: DATA_TABLE_MIN_RESIZE_WIDTH },
    // No class transformation needed here because inline styles already win specificity.
    getRowStyle: wrapGetRowStyleForAgGrid(tableOptions.getRowStyle),
    getRowClass: wrapGetRowClassForAgGrid(tableOptions.getRowClass),
    enableCellTextSelection: true,
    ensureDomOrder: true,
    // Infinite scroll: the client-side row model virtualises rows, so even very
    // large tables (10k+ rows) scroll smoothly. No pager controls; the footer
    // above signals completeness. `pageSize` is accepted on the options type for
    // backwards-compat with existing render JS but no longer controls paging.
    // The one exception is the mobile drill-down modal: pagination stays on
    // (paging bar hidden) pinned to page 0, and the "Load more" footer grows the
    // page size so rows accumulate.
    pagination: loadMoreMode,
    paginationPageSize: loadMoreMode ? visibleCount : undefined,
    paginationPageSizeSelector: false,
    // Re-seed the quick filter so a re-render (theme switch, slicer change,
    // refresh) keeps the rendered rows in sync with the still-visible search box.
    quickFilterText: seededQuickFilter,
    // AG Grid's default tooltip delay is 2000ms; surface it immediately so the
    // "reveal truncated text on hover" use-case feels responsive.
    tooltipShowDelay: 0,
    // This is what gates the HEADERS. `headerTooltip` is a static string with no getter to
    // return "" from, so AG Grid's own measurement (clientWidth against scrollWidth) is the only
    // thing standing between a header that fits and a tooltip restating it.
    //
    // For CELLS it is now a second, redundant gate: each column's tooltipValueGetter already
    // returns "" unless isCellTextClipped says the text is cut off. The two agree on plain
    // columns, and renderer columns skip AG Grid's measurement entirely, so there the JS gate is
    // the only one that runs. Kept anyway rather than dropped, because removing it would
    // un-gate every header in every table.
    //
    // Columns that must always show a tooltip — an explicit `tooltip`, and every drill-down
    // column running the JS reveal gate — exempt themselves from the cell half one at a time by
    // being renderer columns (see buildDefaultCellRenderer). Flipping the whole grid to
    // "standard" instead would take the headers with it.
    //
    // Do NOT add `enableBrowserTooltips` alongside this. Switching to the browser's native
    // `title` attribute silently disables BOTH this option and `tooltipShowDelay` above (AG
    // Grid's own GridOptions typings note it on each), reverting the table to a ~1s browser delay
    // and a tooltip on every cell.
    tooltipShowMode: "whenTruncated" as const,
    // Tooltips render as the reveal panel, which carries a Copy button — so the pointer has to be
    // able to leave the cell and travel into the panel without it vanishing en route. That is
    // exactly what `tooltipInteraction` buys, and the hide delay is what holds the panel open
    // long enough to make the trip. Grid-level and unconditional, because the panel renders in
    // every table now: without it Copy is visible but unreachable.
    //
    // The cost, accepted: tooltips linger after the pointer leaves, hover-only ones included, and
    // paired with `tooltipShowDelay: 0` above that reads as deliberately sticky. It is the price
    // of putting a clickable affordance inside a hover surface.
    tooltipInteraction: true,
    // Touch has no hover to trigger on — and on iOS AG Grid refuses the hover path outright — so
    // those devices switch to the focus trigger, which a tap satisfies by focusing the cell, and
    // get a hide delay measured for a finger rather than a cursor. See usesFocusTooltipTrigger.
    // Pointer devices are untouched: hover, 2s.
    ...(usesFocusTooltipTrigger(targetWindow)
      ? {
          tooltipTrigger: "focus" as const,
          tooltipHideDelay: CELL_REVEAL_TOUCH_HIDE_DELAY_MS,
        }
      : { tooltipHideDelay: CELL_REVEAL_HIDE_DELAY_MS }),
    autoSizeStrategy,
    // Use theme from window if set by the theme system, otherwise use default
    theme:
      targetWindow.__coeffAGGridTheme ??
      agGrid.themeMaterial.withParams({
        browserColorScheme: "light",
        cellHorizontalPaddingScale: 0.9,
        fontSize: 12,
        headerFontSize: 12,
        headerFontWeight: 600,
        headerVerticalPaddingScale: 1.2,
        rowVerticalPaddingScale: 0.9,
        spacing: 4,
      }),
    onGridReady: ({ api: gridApi }) => {
      applySizing(gridApi);
      // NOTE(tommy): When the dashboard content or theme changes, there is an intermittent issue
      // with data table blocks where the table header & data rows are blank (even though the
      // AG Grid container height is correct).
      //
      // Easiest way to reproduce the issue:
      //   1. Comment out the setTimeout below
      //   2. Open the local dev dashboard page in Chrome
      //   3. Open the Chrome Developer Tools panel
      //   4. Switch themes a few times, until you see that AG Grid data rows are blank
      //
      // The most likely cause (not confirmed) is that AG Grid's rendering logic does not
      // fully handle the fact we are rendering AG Grids inside an iframe's srcDoc.
      //
      // Calling the following methods to trigger a re-render of AG Grid fixes the issue.
      setTimeout(() => {
        sizeColumnsToFitIfWideEnough(gridApi, targetWindow, gridContainer);
        gridApi.redrawRows();
        gridApi.refreshHeader();
        applySizing(gridApi);
      });
    },
    // Fires after filtering (quick-filter search) changes the displayed rows —
    // keep the footer count and auto-collapse height in sync.
    onModelUpdated: ({ api: gridApi }) => {
      applySizing(gridApi);
    },
    onFilterChanged: ({ api: gridApi }) => {
      recomputePinnedRows(gridApi);
    },
    // A quick filter re-seeded from session memory on remount is applied without a
    // filterChanged event, so the create-time total (all rows) would go stale.
    onFirstDataRendered: ({ api: gridApi }) => {
      if (seededQuickFilter) {
        recomputePinnedRows(gridApi);
      }
    },
    // Load-more mode: growing the page size re-caps the grid height and
    // refreshes the footer's row range (both inside applySizing).
    onPaginationChanged: ({ api: gridApi }: { api: DataTableSizingGridApi }) => {
      if (loadMoreMode) {
        applySizing(gridApi);
      }
    },
    onGridSizeChanged: ({ api: gridApi }) => {
      sizeColumnsToFitIfWideEnough(gridApi, targetWindow, gridContainer);
      applySizing(gridApi);
    },
    // Keep the frozen-column divider in sync with actual horizontal scroll.
    onBodyScroll: (event: { left?: number; direction?: string }) => {
      if (event.direction === "vertical") {
        return;
      }
      syncPinnedDividerState(event.left ?? 0);
    },
    // Dragging a column header outside the grid hides that column by default in
    // AG Grid — with reorderable columns that's a data-loss foot-gun, not a feature.
    suppressDragLeaveHidesColumns: true,
    // Remember columns the user drags (session memory) and, for editors, persist
    // them into the block's table_overrides so they survive reloads.
    onColumnResized: (event: {
      finished?: boolean;
      source?: string;
      columns?: Array<{
        getColDef?: () => { field?: string };
        getActualWidth?: () => number;
      }> | null;
    }) => {
      // AG Grid caches a cell's tooltip value when the cell renders (updateTooltipText),
      // so the "is this text clipped?" answer goes stale the moment a column changes
      // width - a widened column would keep offering the reveal panel for text that now
      // fits. Re-render the cells once the resize settles so the answer is recomputed.
      // Runs before the width-memory guards below, which no-op for drill-downs. Unconditional,
      // because every table gates its tooltip on that measurement now, not just drill-downs.
      if (event.finished) {
        gridApiRef.current?.refreshCells?.({ force: true });
      }
      if (!resizeMemoryKey || !event.finished) {
        return;
      }
      // Only user-driven resizes (drag/auto-size), not our programmatic fit passes.
      if (typeof event.source !== "string" || !event.source.startsWith("ui")) {
        return;
      }
      const next = { ..._columnWidthMemory.get(resizeMemoryKey) };
      for (const col of event.columns ?? []) {
        const field = col.getColDef?.().field;
        const width = col.getActualWidth?.();
        if (field && typeof width === "number") {
          next[field] = width;
        }
      }
      _columnWidthMemory.set(resizeMemoryKey, next);
      notifyTableOverridesChanged();
    },
    // Same for user-dragged column reorders. `finished` marks the drop; ignore
    // programmatic moves (source is "uiColumnMoved" only for header drags).
    onColumnMoved: (event: { finished?: boolean; source?: string }) => {
      if (!resizeMemoryKey || !event.finished || event.source !== "uiColumnMoved") {
        return;
      }
      const order = readLiveColumnOrder();
      if (order.length > 0) {
        _columnOrderMemory.set(resizeMemoryKey, order);
        notifyTableOverridesChanged();
      }
    },
    // Row drill-down (opt-in via tableOptions.onRowClick). Guards keep the existing cell
    // behaviours intact: links and buttons rendered into cells, the pinned total row, and
    // drag-selecting text (enableCellTextSelection) must not open a drawer.
    // Not wired on drawer tables (no drilldownSource): nested drill-down is unsupported, and a
    // pointer cursor plus a handler whose result is dropped would look like a broken click.
    onCellClicked:
      tableOptions.onRowClick && drilldownSource
        ? (event: {
            data?: Record<string, unknown>;
            rowIndex: number | null;
            colDef?: { field?: string };
            value?: unknown;
            node?: { rowPinned?: string | null };
            event?: Event | null;
          }) => {
            const onRowClick = tableOptions.onRowClick;
            if (!onRowClick || !event.data || event.node?.rowPinned) {
              return;
            }
            const target = event.event?.target as HTMLElement | null | undefined;
            if (target?.closest?.("a, button, input, select, textarea")) {
              return;
            }
            const targetWindow = getTargetWindow();
            if (targetWindow.getSelection?.()?.toString()) {
              return;
            }
            void (async () => {
              try {
                const options = await onRowClick({
                  data: event.data as Record<string, unknown>,
                  rowIndex: event.rowIndex ?? 0,
                  field: event.colDef?.field,
                  value: event.value,
                });
                if (options && typeof options === "object" && drilldownSource) {
                  closeRowDrilldown?.();
                  closeRowDrilldown = createModalDataTable(
                    drilldownSource.cardElement,
                    options,
                    drilldownSource.blockType
                  );
                }
              } catch (error) {
                getTargetWindow().console.error("onRowClick failed:", error);
              }
            })();
          }
        : undefined,
  };

  const gridApi = agGrid.createGrid(gridContainer, agGridOptions);
  if (tableOptions.onRowClick && drilldownSource) {
    gridContainer.style.cursor = "pointer";
  }
  gridApiRef.current = gridApi;

  // ---- Inline header rename (hover pencil → input) --------------------------
  // Renames are another manual adjustment: session-scoped for viewers, persisted
  // into table_overrides for editors via notifyTableOverridesChanged.

  /** Session alias map update; null alias = cleared back to the original name. */
  const setSessionHeaderAlias = (field: string, alias: string | null): void => {
    if (!resizeMemoryKey) {
      return;
    }
    // Seed from the LIVE persisted layer on first edit — the session map is the
    // block's complete alias state from then on (see _columnHeaderMemory). Live
    // lookup, not the creation-time capture, for the same reason as
    // notifyTableOverridesChanged.
    const livePersisted = overridesBlockId ? _tableOverridesByBlockId[overridesBlockId] : undefined;
    const current = {
      ...(_columnHeaderMemory.get(resizeMemoryKey) ?? livePersisted?.column_headers),
    };
    if (alias) {
      current[field] = alias;
    } else {
      delete current[field];
    }
    _columnHeaderMemory.set(resizeMemoryKey, current);
  };

  const commitHeaderRename = (field: string, rawValue: string): void => {
    const alias = rawValue.trim();
    const original = originalHeaderByField.get(field);
    // Clearing the input (or retyping the JS name) removes the override so the
    // JS headerName — or AG Grid's field-derived default — shows again.
    const cleared = alias === "" || (original !== undefined && alias === original);
    // Mutate the grid's own colDef (setGridOption("columnDefs") would re-apply
    // def widths and stomp drags made since creation), and our def copy so
    // CSV export headers stay in sync.
    const liveDef = gridApi.getColumn?.(field)?.getColDef?.();
    for (const def of [liveDef, headerDefs.find(d => d.field === field)]) {
      if (!def) {
        continue;
      }
      if (cleared) {
        if (original === undefined) {
          delete def.headerName;
        } else {
          def.headerName = original;
        }
        def.headerTooltip = original ?? field;
      } else {
        def.headerName = alias;
        def.headerTooltip = alias;
      }
    }
    gridApi.refreshHeader();
    setSessionHeaderAlias(field, cleared ? null : alias);
    notifyTableOverridesChanged();
  };

  const startHeaderRename = (headerCell: HTMLElement, field: string): void => {
    const textEl = headerCell.querySelector<HTMLElement>(".ag-header-cell-text");
    if (!textEl || headerCell.querySelector(".coeff-header-rename-input")) {
      return;
    }
    const input = targetDocument.createElement("input");
    input.type = "text";
    input.name = "coeff-header-rename";
    input.className = "coeff-header-rename-input";
    input.value = textEl.textContent ?? "";
    let cancelled = false;
    // Keep AG Grid's header interactions (sort on click, column drag on
    // mousedown, keyboard nav) away from the editor.
    for (const type of ["click", "dblclick", "mousedown", "touchstart"]) {
      input.addEventListener(type, e => e.stopPropagation());
    }
    input.addEventListener("keydown", e => {
      e.stopPropagation();
      if (e.key === "Enter") {
        input.blur();
      } else if (e.key === "Escape") {
        cancelled = true;
        input.blur();
      }
    });
    input.addEventListener("blur", () => {
      const raw = input.value;
      textEl.style.display = "";
      input.remove();
      if (!cancelled) {
        commitHeaderRename(field, raw);
      }
    });
    textEl.style.display = "none";
    textEl.insertAdjacentElement("beforebegin", input);
    input.focus();
    input.select();
  };

  // Lazy injection via hover delegation: header cells are virtualised and
  // recreated by AG Grid (scroll, refreshHeader), so the pencil is (re)attached
  // to whichever cell the user actually hovers. CSS reveals it on header hover.
  const headerRenameHoverHandler = (event: Event): void => {
    const target = event.target as HTMLElement | null;
    const headerCell = target?.closest?.<HTMLElement>(".ag-header-cell");
    if (!headerCell || headerCell.querySelector(".coeff-header-rename-btn")) {
      return;
    }
    const field = headerCell.getAttribute("col-id");
    const label = headerCell.querySelector(".ag-header-cell-label");
    // Only real data columns we built defs for (originalHeaderByField is the
    // authoritative field set).
    if (!field || !label || !originalHeaderByField.has(field)) {
      return;
    }
    const button = targetDocument.createElement("button");
    button.type = "button";
    button.className = "coeff-header-rename-btn material-icons";
    button.textContent = "edit";
    button.title = "Rename column";
    button.setAttribute("aria-label", "Rename column");
    button.addEventListener("mousedown", e => e.stopPropagation());
    button.addEventListener("touchstart", e => e.stopPropagation());
    button.addEventListener("click", e => {
      e.stopPropagation();
      e.preventDefault();
      startHeaderRename(headerCell, field);
    });
    label.appendChild(button);
  };
  if (resizeMemoryKey) {
    gridContainer.addEventListener("mouseover", headerRenameHoverHandler);
  }

  const exportApi: DataTableExportApi = {
    getCsvText: () => buildCsvText("\n", true),
    copyCsvToClipboard: async () => {
      await writeTextToClipboardWithFallback(
        targetWindow,
        targetDocument,
        buildCsvText("\n", true)
      );
    },
    downloadCsv: () => {
      const filename = `${getExportTitle()}.csv`;
      downloadTextFile(
        targetWindow,
        targetDocument,
        filename,
        buildCsvText("\r\n", true),
        "text/csv;charset=utf-8"
      );
    },
    setQuickFilter: (text: string) => {
      gridApi.setGridOption("quickFilterText", text);
      // Remember it so a subsequent re-render re-seeds the same filter (keeps the
      // React search box and the rendered rows in sync). See _quickFilterMemory.
      if (resizeMemoryKey) {
        if (text) {
          _quickFilterMemory.set(resizeMemoryKey, text);
        } else {
          _quickFilterMemory.delete(resizeMemoryKey);
        }
      }
    },
  };

  // Attach export API to the card element so hover controls can access it.
  const cardElement =
    dataTableContainer.closest<CoeffDataTableExportHost>(".themed-dashboard-card");
  (dataTableContainer as CoeffDataTableExportHost).__coeffDataTableExportApi = exportApi;
  if (cardElement) {
    cardElement.__coeffDataTableExportApi = exportApi;
  }

  if (showActionsInTable) {
    const createIconButton = (iconName: string, label: string, onClick: () => void) => {
      const button = targetDocument.createElement("button");
      button.type = "button";
      button.className = "themed-data-table-action-button";
      button.setAttribute("aria-label", label);
      button.title = label;
      button.addEventListener("click", e => {
        e.preventDefault();
        onClick();
      });

      const icon = targetDocument.createElement("span");
      icon.className = "material-icons";
      icon.textContent = iconName;
      button.appendChild(icon);

      return button;
    };

    const actionsWrapper = targetDocument.createElement("div");
    // Class lets mobile CSS hide the actions (Edit/Copy/Download) in the drill-down modal.
    actionsWrapper.className = "themed-data-table-actions-wrapper";
    actionsWrapper.style.position = "absolute";
    actionsWrapper.style.top = "-44px";
    actionsWrapper.style.right = "0";
    actionsWrapper.style.zIndex = "10";
    actionsWrapper.style.display = "flex";
    actionsWrapper.style.gap = "8px";
    dataTableContainer.appendChild(actionsWrapper);

    {
      const button = createIconButton("edit", "Edit", () => {
        const chip = (dataTableContainer as DrilldownChatChipHost).__coeffDrilldownChatChip;
        if (chip && targetWindow.openDashboardChatWithBlockChip) {
          targetWindow.openDashboardChatWithBlockChip(
            chip.blockId,
            chip.blockTitle,
            chip.blockType
          );
        } else {
          targetWindow.openDashboardChat?.();
        }
      });
      actionsWrapper.appendChild(button);
    }
    {
      const button = createIconButton("content_copy", "Copy", () => {
        void exportApi.copyCsvToClipboard();
      });
      actionsWrapper.appendChild(button);
    }
    {
      const button = createIconButton("save_alt", "Download CSV", () => {
        exportApi.downloadCsv();
      });
      actionsWrapper.appendChild(button);
    }
  }

  // Handle copy events to format data for spreadsheet paste
  const copyHandler = (e: ClipboardEvent) => {
    const selection = targetWindow.getSelection();
    if (!selection || selection.isCollapsed) {
      return;
    }

    // Check if selection is within the grid
    const range = selection.getRangeAt(0);
    if (!gridContainer.contains(range.commonAncestorContainer)) {
      return;
    }

    // Parse selected cells from the DOM
    const selectedCells = parseSelectedCells(gridContainer, selection);
    if (selectedCells.length === 0) {
      return;
    }

    // Format as TSV (tabs between columns, newlines between rows)
    const tsv = selectedCells.map(row => row.join("\t")).join("\n");

    e.preventDefault();
    e.clipboardData?.setData("text/plain", tsv);
  };
  gridContainer.addEventListener("copy", copyHandler);

  // Wire up search input to quick filter
  if (searchInput) {
    // Function to update search box state (collapsed vs expanded)
    const updateSearchBoxState = () => {
      if (!searchInput) {
        return;
      }

      const isEmpty = searchInput.value === "";
      const isFocused = targetDocument.activeElement === searchInput;
      const shouldCollapse = isEmpty && !isFocused;

      if (shouldCollapse) {
        searchInput.classList.add("themed-data-table-search-collapsed");
        searchInput.placeholder = "";
      } else {
        searchInput.classList.remove("themed-data-table-search-collapsed");
        searchInput.placeholder = "Search";
      }
    };

    // Initialize as collapsed (empty and not focused)
    updateSearchBoxState();

    // Create debounced function for filter updates. Mirrors into the session
    // quick-filter memory (like the block-chrome search's setQuickFilter) so a
    // re-render — theme switch, slicer change — re-seeds the same filter.
    debouncedFilterUpdate = debounce((value: string) => {
      gridApi.setGridOption("quickFilterText", value);
      if (resizeMemoryKey) {
        if (value) {
          _quickFilterMemory.set(resizeMemoryKey, value);
        } else {
          _quickFilterMemory.delete(resizeMemoryKey);
        }
      }
    }, 200);

    searchInputHandler = (e: Event) => {
      const value = (e.target as HTMLInputElement).value;

      // Immediate UI updates (no debounce)
      // Show/hide clear button based on input content
      if (clearButton) {
        clearButton.style.display = value ? "block" : "none";
      }
      // Update search box state based on value
      updateSearchBoxState();

      // Debounced filter update (500ms delay)
      if (debouncedFilterUpdate) {
        debouncedFilterUpdate(value);
      }
    };
    searchInput.addEventListener("input", searchInputHandler);

    // Escape clears the search immediately — same semantics as the block-chrome
    // search on desktop, so the control behaves identically across platforms.
    searchKeydownHandler = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || !searchInput) {
        return;
      }
      if (debouncedFilterUpdate) {
        debouncedFilterUpdate.cancel();
      }
      searchInput.value = "";
      if (clearButton) {
        clearButton.style.display = "none";
      }
      gridApi.setGridOption("quickFilterText", "");
      if (resizeMemoryKey) {
        _quickFilterMemory.delete(resizeMemoryKey);
      }
      searchInput.blur();
      updateSearchBoxState();
    };
    searchInput.addEventListener("keydown", searchKeydownHandler);

    // Add focus and blur event listeners to update state
    searchFocusHandler = updateSearchBoxState;
    searchBlurHandler = updateSearchBoxState;
    searchInput.addEventListener("focus", searchFocusHandler);
    searchInput.addEventListener("blur", searchBlurHandler);

    // Wire up clear button click handler
    if (clearButton) {
      clearButton.addEventListener("click", () => {
        if (searchInput) {
          // Cancel any pending debounced filter update
          if (debouncedFilterUpdate) {
            debouncedFilterUpdate.cancel();
          }
          searchInput.value = "";
          clearButton!.style.display = "none";
          // Immediately update filter (no debounce when clearing)
          gridApi.setGridOption("quickFilterText", "");
          if (resizeMemoryKey) {
            _quickFilterMemory.delete(resizeMemoryKey);
          }
          // Update state after clearing
          updateSearchBoxState();
        }
      });
    }
  }

  // Debounced: the container ResizeObserver below handles container-driven size
  // changes; this listener covers window resizes that change sizing BEHAVIOR
  // without changing the container — crossing the 640px
  // sizeColumnsToFitIfWideEnough threshold, where columns must re-fit even though
  // the container width may be unchanged.
  const windowResizeHandler = debounce(() => {
    sizeColumnsToFitIfWideEnough(gridApi, targetWindow, gridContainer);
    applySizing(gridApi);
  }, 100);
  targetWindow.addEventListener("resize", windowResizeHandler);

  // The grid container carries an explicit pixel height, so when the BLOCK grows
  // — a vertical drag of the row's resize handle, a neighbouring row collapsing,
  // the chat sidebar toggling — AG Grid's onGridSizeChanged never fires (the grid
  // itself didn't change size) and the block filled with dead whitespace below a
  // stale-height grid. Observe the flex container the layout actually drives and
  // re-run sizing when it changes; one pass per animation frame makes the grid
  // track a drag live while coalescing notification bursts (applySizing forces
  // synchronous layout reads). Column fitting is a full AG Grid relayout and only
  // depends on width, so width-driven fits run on the same settle cadence as
  // windowResizeHandler instead of per frame. No observer loop: applySizing
  // writes the grid child's height, never the observed container's.
  const debouncedContainerColumnFit = debounce(() => {
    // Passes gridContainer: this observer fires precisely when the CONTAINER changed size while the
    // window did not, which is the case the container-width rule in sizeColumnsToFitIfWideEnough
    // exists for. Omitting it would leave this the one path still judging room by viewport alone.
    sizeColumnsToFitIfWideEnough(gridApi, targetWindow, gridContainer);
  }, 100);
  const ResizeObserverCtor =
    (targetWindow as unknown as { ResizeObserver?: typeof ResizeObserver }).ResizeObserver ??
    (typeof ResizeObserver !== "undefined" ? ResizeObserver : undefined);
  let containerResizeObserver: ResizeObserver | null = null;
  if (ResizeObserverCtor) {
    let lastContainerWidth = dataTableContainer.clientWidth;
    let sizingPassQueued = false;
    containerResizeObserver = new ResizeObserverCtor(() => {
      if (sizingPassQueued) {
        return;
      }
      sizingPassQueued = true;
      targetWindow.requestAnimationFrame(() => {
        sizingPassQueued = false;
        const width = dataTableContainer.clientWidth;
        if (width !== lastContainerWidth) {
          lastContainerWidth = width;
          debouncedContainerColumnFit();
        }
        applySizing(gridApi);
      });
    });
    containerResizeObserver.observe(dataTableContainer);
    // Also observe the elements sizeDataTableGrid measures. AG Grid settles some
    // of them asynchronously after our last wired event — the horizontal
    // scrollbar's height (0 until overflow is confirmed; on always-visible
    // scrollbar systems it then consumes real rows space) and the rows
    // container's height (grows as the initial render completes). A sizing pass
    // that runs before they settle bakes stale numbers into the grid height, and
    // neither onGridSizeChanged nor the container observer fires afterwards to
    // correct it — the visible symptom is the last row half-hidden under an
    // always-on horizontal scrollbar. The elements all exist in AG Grid's DOM
    // shell from createGrid, so a one-shot query here is safe; the no-op height
    // write in sizeDataTableGrid keeps the observe → size → observe cycle from
    // looping.
    for (const selector of [
      ".ag-header",
      ".ag-center-cols-container",
      ".ag-body-horizontal-scroll",
      ".ag-floating-bottom",
    ]) {
      const measuredEl = gridContainer.querySelector<HTMLElement>(selector);
      if (measuredEl) {
        containerResizeObserver.observe(measuredEl);
      }
    }
    // The footer's height feeds fill-mode availableHeight; it can reflow (e.g.
    // the row-count text arriving) without resizing the flex container above it.
    containerResizeObserver.observe(footer);
  }

  return () => {
    closeRowDrilldown?.();
    delete (dataTableContainer as CoeffDataTableExportHost).__coeffDataTableExportApi;
    if (cardElement) {
      delete cardElement.__coeffDataTableExportApi;
    }
    gridContainer.removeEventListener("copy", copyHandler);
    gridContainer.removeEventListener("mouseover", headerRenameHoverHandler);
    // Cancel any pending debounced filter update
    if (debouncedFilterUpdate) {
      debouncedFilterUpdate.cancel();
    }
    if (searchInput) {
      if (searchInputHandler) {
        searchInput.removeEventListener("input", searchInputHandler);
      }
      if (searchKeydownHandler) {
        searchInput.removeEventListener("keydown", searchKeydownHandler);
      }
      if (searchFocusHandler) {
        searchInput.removeEventListener("focus", searchFocusHandler);
      }
      if (searchBlurHandler) {
        searchInput.removeEventListener("blur", searchBlurHandler);
      }
    }
    if (loadMoreButton && loadMoreClickHandler) {
      loadMoreButton.removeEventListener("click", loadMoreClickHandler);
    }
    windowResizeHandler.cancel();
    targetWindow.removeEventListener("resize", windowResizeHandler);
    debouncedContainerColumnFit.cancel();
    containerResizeObserver?.disconnect();
    blockRowCount?.remove();
    gridApi.destroy();
  };
}

/**
 * Creates an AG Grid data table inside a dashboard block.
 */
export function createDataTable(
  cardElement: HTMLElement,
  tableOptions: DataTableOptions
): CleanupFunction {
  let dataTableContainer = cardElement.querySelector<HTMLElement>(".data-table-container");

  // Recover from AI-generated render JS that overwrites cardElement.innerHTML
  // (e.g. to inject a custom heading) and wipes the container DataTableBlock
  // pre-installs. Re-create it instead of throwing — keep the classes/attribute
  // in sync with DataTableBlock.tsx so theming/sizing/Sentry masking still apply.
  if (!dataTableContainer) {
    const doc = cardElement.ownerDocument;
    dataTableContainer = doc.createElement("div");
    dataTableContainer.className = "data-table-container flex-1 min-h-0 ag-theme-material";
    dataTableContainer.setAttribute("data-sentry-mask", "");
    cardElement.appendChild(dataTableContainer);
  }

  // Standalone table blocks already have hover actions (edit/menu) from the outer
  // block UI, and on desktop the search box lives in that same action row
  // (DraggableBlock), driven via the exposed setQuickFilter — so render neither
  // inside the table itself. On mobile that whole action cluster is hidden
  // (hideBlockActions), so the in-table search mounts instead; the applyTheme
  // mobile CSS restyles it as a pre-expanded full-width bar below the title.
  const targetWindow = getTargetWindow();
  const isMobileViewport = targetWindow.innerWidth < AG_GRID_SIZE_TO_FIT_MIN_WIDTH;
  return createDataTableInternal(dataTableContainer, tableOptions, {
    showActionsInTable: false,
    showSearch: isMobileViewport,
    drilldownSource: { cardElement, blockType: "data_table" },
  });
}

/**
 * Creates a modal data table for chart drill-down.
 */
export function createModalDataTable(
  cardElement: HTMLElement,
  tableOptions: ModalDataTableOptions,
  // The block type of the source the drill-down was launched from, so edit-in-chat
  // references the right block (e.g. "metric_card" for a KPI card, not "chart").
  blockType = "chart"
): CleanupFunction {
  const targetWindow = getTargetWindow();
  const targetDocument = targetWindow.document;

  const sourceBlockId = cardElement.id?.startsWith("block_")
    ? cardElement.id.replace(/^block_/, "")
    : "";
  const sourceTitle =
    cardElement.querySelector(".themed-card-title")?.textContent?.trim() ??
    cardElement.querySelector("h2")?.textContent?.trim() ??
    "";
  const drilldownTitleBase = sourceTitle || tableOptions.title;
  const drilldownChip: DrilldownChatChip | null =
    sourceBlockId && drilldownTitleBase
      ? {
          // Use the source block's id (stable), but make the label clearly drilldown-specific.
          blockId: sourceBlockId,
          blockTitle: `${drilldownTitleBase}-drilldown`,
          blockType,
        }
      : null;

  const h2 = targetDocument.createElement("h2");
  // On mobile the search/actions no longer sit inline at the top-right (see the .data-table-modal
  // mobile rules in applyTheme.ts). Reserve just enough right margin to clear the drawer's floating
  // close X (the drawer's mobile padding drops mx-8, so the title would otherwise run under it).
  h2.className =
    "text-lg font-semibold mb-4 mr-[calc(var(--data-table-search-width)+var(--data-table-actions-offset))] max-sm:mr-10";
  h2.style.color = "inherit";
  h2.textContent = tableOptions.title;

  const dataTableContainer = targetDocument.createElement("div") as DrilldownChatChipHost;
  // `data-table-modal` marks this as the drill-down modal table so mobile CSS can strip it down to
  // just a full-width search (hiding the action buttons) without affecting regular table blocks.
  dataTableContainer.className =
    "data-table-container data-table-modal min-h-[300px] ag-theme-material";
  if (drilldownChip) {
    dataTableContainer.__coeffDrilldownChatChip = drilldownChip;
  }

  const cleanup = createDataTableInternal(dataTableContainer, tableOptions, {
    sizeHeightToFit: true,
    showActionsInTable: true,
  });

  // Call the global setBottomDrawerContent function to show the modal
  targetWindow.setBottomDrawerContent?.([h2, dataTableContainer]);

  return () => {
    delete dataTableContainer.__coeffDrilldownChatChip;
    cleanup();
  };
}

/**
 * Wires click-to-drill-down onto a metric card and returns its cleanup.
 *
 * Unlike charts (which get an `onClick` hook from Chart.js), a metric card has
 * no built-in click handler, so this owns the otherwise-repetitive lifecycle:
 * the pointer cursor, the click listener, an in-flight guard so a re-render
 * mid-query can't open a stale drawer, and tearing down the open drawer on
 * cleanup. `render_function_js` supplies `loadDrilldown` (fetch the constituent
 * rows, return the modal table options) and returns this function's result.
 */
export function createMetricCardDrilldown(
  cardElement: HTMLElement,
  loadDrilldown: () => Promise<ModalDataTableOptions | null | undefined>
): CleanupFunction {
  return createDrilldown(cardElement, loadDrilldown, "metric_card");
}

/**
 * The general form of `createMetricCardDrilldown`: wire click-to-drill-down onto ANY element a
 * render function owns — a custom_html tile, a row it drew, a legend chip. The drawer's edit
 * chip is attributed to the block that contains `element`, found by walking up to the
 * `block_<id>` card; pass `blockType` so that chip names the right block type.
 */
export function createDrilldown(
  element: HTMLElement,
  loadDrilldown: () => Promise<ModalDataTableOptions | null | undefined>,
  blockType = "custom_html"
): CleanupFunction {
  let closeModal: CleanupFunction | null = null;
  let disposed = false;
  const cardElement =
    (element.id?.startsWith("block_") ? element : element.closest<HTMLElement>('[id^="block_"]')) ??
    element;

  const onClick = async () => {
    const options = await loadDrilldown();
    // Re-rendered while the query was in flight, or nothing to show.
    if (disposed || !options) {
      return;
    }
    closeModal?.(); // tear down any prior drill-down grid before opening a new one
    closeModal = createModalDataTable(cardElement, options, blockType);
  };

  const priorCursor = element.style.cursor;
  element.style.cursor = "pointer";
  element.addEventListener("click", onClick);

  return () => {
    disposed = true;
    element.style.cursor = priorCursor;
    element.removeEventListener("click", onClick);
    closeModal?.();
  };
}

// --------------------------------
// Hook to expose dashboard helpers globally
// --------------------------------

export function useDashboardWindowGlobals(
  runSQL: RunSQLFn,
  targetWindow: DashboardWindow,
  jsHelperFunctions: Record<string, string> = {},
  viewerEmail: string | null = null,
  setSlicerValue?: (slicerName: string, value: SlicerValue) => void,
  dashboardId?: string,
  tableOverrides?: {
    /** Persisted `table_overrides` per data_table block id, from the content spec. */
    byBlockId: Record<string, DataTableUserOverrides>;
    /**
     * Per data_table block: a fingerprint of the state session drags are layered
     * over (`JSON(table_overrides)|render_function_js`). A change invalidates
     * that block's session memory so stale drags don't shadow an AI edit.
     */
    fingerprintByBlockId: Record<string, string>;
    /** Editor-only save hook; omit for view-only users (drags stay session-scoped). */
    onChange?: (blockId: string, overrides: DataTableUserOverrides) => void;
  },
  /**
   * Receives each data table's natural card content height after sizing, so the
   * layout can auto-collapse table-only rows (see computeEffectiveRowHeight).
   */
  onTableContentHeight?: (blockId: string, heights: TableContentHeightReport) => void,
  /** Persisted `chart_overrides` per chart block id, from the content spec. */
  chartOverridesByBlockId?: Record<string, ChartUserOverrides>
): void {
  useLayoutEffect(() => {
    // Set the module-level reference so helper functions can access it
    _targetWindow = targetWindow;
    _dataTableMemoryNamespace = dashboardId ?? null;
    _tableOverridesByBlockId = tableOverrides?.byBlockId ?? {};
    _chartOverridesByBlockId = chartOverridesByBlockId ?? {};
    _onTableOverridesChange = tableOverrides?.onChange ?? null;
    _onTableContentHeight = onTableContentHeight ?? null;
    // Must run BEFORE the blocks' render effects recreate their grids (layout
    // effect vs passive effect ordering) so stale session drags are gone by the
    // time a grid re-reads them.
    invalidateStaleTableSessionMemory(tableOverrides?.fingerprintByBlockId ?? {});

    // Parse helper functions in the iframe's context so they have access to iframe globals
    const helpers: Record<string, unknown> = {};
    for (const [name, fnString] of Object.entries(jsHelperFunctions)) {
      try {
        // Strip trailing semicolon if present (function expressions shouldn't have semicolons when wrapped in return)
        const cleanedFnString = fnString.trim().replace(/;\s*$/, "");
        // eslint-disable-next-line no-new-func
        const sourceUrl = `${encodeURIComponent(name)}__js_helper_function`;
        helpers[name] = new targetWindow.Function(
          `return (${cleanedFnString});\n//# sourceURL=${sourceUrl}\n`
        )();
      } catch (error) {
        // Log to the iframe console so console capture can detect it.
        targetWindow.console.error(`Failed to parse helper function ${name}:`, error);
      }
    }

    // Expose helper functions globally on the iframe window for render_function_js to use
    targetWindow.Helpers = helpers;
    targetWindow.runSQL = runSQL;
    targetWindow.createChart = createChart;
    targetWindow.createDataTable = createDataTable;
    targetWindow.createModalDataTable = createModalDataTable;
    targetWindow.createMetricCardDrilldown = createMetricCardDrilldown;
    targetWindow.createDrilldown = createDrilldown;
    targetWindow.sortChartDataByDateLabels = sortChartDataByDateLabels;
    targetWindow.parseDateLabelToSortKey = parseDateLabelToSortKey;
    targetWindow.formatChartNumber = formatChartNumber;
    targetWindow.truncateChartLabel = truncateChartLabel;
    targetWindow.interpolateHeatmapColor = interpolateHeatmapColor;
    targetWindow.normalizeGeoName = normalizeGeoName;
    targetWindow.buildGeoValueMap = buildGeoValueMap;
    targetWindow.slicerClause = slicerClause;
    if (setSlicerValue) {
      targetWindow.setSlicerValue = setSlicerValue;
    }
    targetWindow.__coeff_personalized_view = {
      email: viewerEmail ?? null,
    };

    return () => {
      _targetWindow = null;
      _dataTableMemoryNamespace = null;
      _tableOverridesByBlockId = {};
      _chartOverridesByBlockId = {};
      _onTableOverridesChange = null;
      _onTableContentHeight = null;
      targetWindow.Helpers = {};
      delete targetWindow.runSQL;
      delete targetWindow.createChart;
      delete targetWindow.createDataTable;
      delete targetWindow.createModalDataTable;
      delete targetWindow.createMetricCardDrilldown;
      delete targetWindow.createDrilldown;
      delete targetWindow.sortChartDataByDateLabels;
      delete targetWindow.parseDateLabelToSortKey;
      delete targetWindow.formatChartNumber;
      delete targetWindow.truncateChartLabel;
      delete targetWindow.interpolateHeatmapColor;
      delete targetWindow.normalizeGeoName;
      delete targetWindow.buildGeoValueMap;
      delete targetWindow.slicerClause;
      delete targetWindow.setSlicerValue;
      delete targetWindow.__coeff_personalized_view;
    };
  }, [
    runSQL,
    targetWindow,
    jsHelperFunctions,
    viewerEmail,
    setSlicerValue,
    dashboardId,
    tableOverrides,
    onTableContentHeight,
    chartOverridesByBlockId,
  ]);
}

// --------------------------------
// Helper Functions
// --------------------------------

// Wrapped as an expression, so a trailing `;` (e.g. `async () => {...};`) would be a syntax error.
function toFunctionExpression(fnString: string): string {
  return fnString.trim().replace(/;+\s*$/, "");
}

/**
 * Safely evaluate a JavaScript function string and call it.
 * Used for evaluating AI-generated default value functions.
 */
export function evalAndCall<T>(fnString: string): T {
  // eslint-disable-next-line no-new-func, @typescript-eslint/no-implied-eval
  const fn = new Function(`return (${toFunctionExpression(fnString)})`)();
  return fn();
}

/**
 * Async variant of evalAndCall that passes runSQL as the first argument.
 * Supports both async (runSQL) => ... and legacy sync () => ... functions.
 */
export async function evalAndCallAsync<T>(fnString: string, runSQL: RunSQLFn): Promise<T> {
  // eslint-disable-next-line no-new-func, @typescript-eslint/no-implied-eval
  const fn = new Function(`return (${toFunctionExpression(fnString)})`)() as (
    runSQL: RunSQLFn
  ) => T | Promise<T>;
  return await Promise.resolve(fn(runSQL));
}

export const toBareDate = (date: Date | string): Date => {
  // Ensure we have a Date object (might be a string from serialization)
  const d = date instanceof Date ? date : new Date(date);
  // Date-only strings (e.g. "2025-12-01") are parsed as UTC midnight by the JS spec.
  // If UTC time is midnight, use UTC date parts to get the intended date.
  // Otherwise (datetime with time component), use local date parts.
  if (d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0) {
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  }
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const isDateLike = (value: unknown): value is Date =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as Date).getTime === "function" &&
  !isNaN((value as Date).getTime());

/**
 * Re-mint date-range slicer values with the iframe realm's Date constructor before handing
 * slicerState to a render function. Render functions are compiled with the iframe's Function
 * constructor, so `instanceof Date` on a parent-realm Date is false there — AI-generated blocks
 * that guard with `startDate instanceof Date` would silently fall back to hardcoded date windows
 * and ignore the slicer. Also coerces string endpoints (e.g. from a custom_html block calling
 * setSlicerValue with ISO strings) into bare dates.
 */
export const normalizeSlicerStateForRealm = (
  slicerState: SlicerState,
  targetWindow: DashboardWindow
): SlicerState => {
  const TargetDate = targetWindow.Date ?? Date;

  const normalizeEndpoint = <T>(value: T): T | Date => {
    if (isDateLike(value)) {
      return new TargetDate(value.getTime());
    }
    if (typeof value === "string") {
      const bare = toBareDate(value);
      if (!isNaN(bare.getTime())) {
        return new TargetDate(bare.getTime());
      }
    }
    return value;
  };

  const normalized: SlicerState = {};
  for (const [name, value] of Object.entries(slicerState)) {
    if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      normalized[name] = {
        ...value,
        startDate: normalizeEndpoint(value.startDate),
        endDate: normalizeEndpoint(value.endDate),
      };
    } else {
      normalized[name] = value;
    }
  }
  return normalized;
};
